// pipe: 4A.6 ERP records service — talks to the cookie-authed Core API (same-origin, credentials:'include').
import { LiveData, Service } from '@toeverything/infra';

// TYPE-ONLY (erased at build — no runtime edge, the `modules/peek-view → desktop/pages` precedent):
// the Item Passport wire contract is declared ONCE beside the screen that owns it, so the service
// and the page cannot drift into two copies of the same response shape.
import type {
  AssistantHistory,
  AssistantMessage,
  AssistantMode,
  AssistantPage,
} from '../../../desktop/pages/workspace/ask/assistant-types';
import type {
  Passport,
  PassportQuery,
} from '../../../desktop/pages/workspace/records/passport-types';
import type { DirectorSourceSummaries } from '../director-source-types';
import { scopeCacheKey, siteScopeHeaders } from '../erp-site-scope';
import type {
  MondayConfig,
  MondayItemPage,
  MondaySourceDetail,
  MondayStatus,
} from '../monday-types';
import { requestErpRefresh } from '../refresh';
import { readSseEvents } from '../sse';

export interface FieldSchema {
  name: string;
  type:
    | 'string'
    | 'number'
    | 'boolean'
    | 'uuid'
    | 'timestamp'
    | 'enum'
    | 'array'
    | 'object';
  required: boolean;
  enum_values?: string[];
  sensitive: boolean;
}
export interface LifecycleSchema {
  state_field: string;
  states: string[];
  edges: [string, string][];
}
/** 4C: the search modes the Core `search_core` supports. `keyword` = ILIKE substring (the global
 *  palette default); `semantic` = pgvector (needs the provisioned embedder); `graph` = relation-expanded. */
export type SearchMode = 'keyword' | 'semantic' | 'graph';
export interface RecordSearchPage {
  rows: RecordRow[];
  has_more: boolean;
  next_offset: number | null;
}
export interface EntitySchema {
  type_name: string;
  allow_unknown_fields: boolean;
  title_sensitive: boolean;
  fields: FieldSchema[];
  lifecycle?: LifecycleSchema;
  /** The station this type belongs to (`rbac::stage_for_type` — the same public type→station map
   *  the row serializer already emits). Absent for cross-cutting types. Lets a STAGE-keyed screen
   *  (the Operations work queue) derive its own type set from the ONE registry map instead of
   *  hardcoding a second copy of it. */
  stage?: string | null;
}
export interface RecordRow {
  search_match?: 'exact' | 'prefix' | 'contains' | 'near';
  id: string;
  type: string;
  title: string | null;
  title_redacted?: boolean;
  data: Record<string, unknown>;
  /** 4B.6: the non-sensitive station the type belongs to (server-computed; null for cross-cutting types). */
  stage?: string | null;
  /** 4B.6: the legal next-states the CURRENT actor may advance to (server-SSOT; gated by stage authority). */
  allowed_transitions?: string[];
  /** 4B.5 ownership axis: the customer tenant column (internal projection only; null when untagged). */
  account_id?: string | null;
  /** 4B.5 dispatch axis: the LOGIN this row is assigned to — a Keycloak subject id, NOT an employee
   *  entity id (internal + Driver projections only; null when unassigned). Set through the audited
   *  `may_assign_driver`-gated path only (`assignPickup`), never a plain field edit. */
  assigned_to?: string | null;
  created_at?: string;
  updated_at?: string;
  /** P0.7 (inbound_order rows only): may the CURRENT actor issue a CoD/CoR for this order?
   *  Server-SSOT (the same rbac gate the issuance endpoints enforce) — render the issue buttons
   *  STRICTLY from this flag, never re-derive roles client-side (the 4B.6 rule). */
  can_issue_certificates?: boolean;
  /** P3.5 (inbound_order rows only): may the CURRENT actor print the sanitization-evidence
   *  documents (OSDW/OSDS — drive serials + wipe records)? Server-SSOT, same rule as above. */
  can_print_sanitization_docs?: boolean;
  /** P1.5: derived SLA/deliverable clocks (worklist enrichment — inbound_order rows only; advisory). */
  sla?: OrderSlaClocks | null;
}

/** One page of a list read plus the server's exact "there is more" verdict (`x-list-truncated`).
 *  `truncated` is computed server-side from a probe row BEYOND the page, so it is not the
 *  "did I get exactly `limit` rows?" guess — a page that happens to be exactly full is not capped. */
export interface RecordPage<T = RecordRow> {
  rows: T[];
  truncated: boolean;
}

/** P0.7: the certificate-issuance response metadata (the signed PDF itself stays server-side). */
export interface CertificateIssueResult {
  id: string;
  cert_number: string;
  pdf_sha256: string;
  issued_at: string;
}

/** The counts-only result of a per-drive Ziperase wipe-evidence pull (never a serial/row — the
 *  server report is aggregates + a mode flag). `dry_run` is true for a receiver's probe. */
export interface WipeEvidenceResult {
  dry_run: boolean;
  fetched: number;
  matched_drive: number;
  created: number;
  already_recorded: number;
  /** hardware_scan rows captured this pull (disk on a wipe pull; machine on an asset pull). */
  hardware_captured?: number;
  /** erasure-report PDF certificates filed as evidence this pull (only when `?certificate=1` was
   *  requested — the passport buttons; 0 on the intake auto-scan or an idempotent re-file). */
  certificate_captured?: number;
}

/** P1.5: the derived SLA/deliverable due-date clocks for one inbound order (Monday §3 formulas —
 *  computed server-side, never stored; dates + day counts only, never a customer name). */
export interface OrderSlaClocks {
  received_date: string;
  sla_days: number;
  /** Where the SLA came from: `agreement` | `client_seed` | `default`. */
  sla_source: string;
  edp_due?: string | null;
  edp_overdue?: boolean | null;
  inventory_report_due?: string | null;
  inventory_report_overdue?: boolean | null;
  sales_due: string;
  sales_overdue: boolean;
  final_report_due: string;
  final_report_overdue: boolean;
}

/** P1.5: the GET /api/ops/orders/:id/sla payload (`sla` is null until the order is received). */
export interface OrderSlaReport {
  order_id: string;
  order_no?: string | null;
  sla: OrderSlaClocks | null;
}

// 4B.9: a Customers-list account row — the SSOT-redacted account value PLUS the server-computed
// `owner_manageable` affordance flag. Clients render the assign/transfer/revoke controls STRICTLY
// from this flag (the enshrined 4B.6 rule — NEVER re-derive "am I owner/admin" client-side).
export interface AccountRow extends RecordRow {
  /** Server-SSOT: may the CURRENT actor set/change/clear this account's owner? Drives the affordance. */
  owner_manageable: boolean;
}

// --- CRM derived read-model DTOs (field-for-field with `crm.rs`; 4C.2 — advisory, aggregate-only) --
/** Pickup + processing SLA scorecard for one account (Erp-readable — no sensitive scalar). */
export interface CrmSlaReport {
  account_id: string;
  window_days: number | null;
  orders_considered: number;
  pickup_on_time: number;
  pickup_late: number;
  pickup_on_time_pct: number | null;
  avg_processing_days: number | null;
  processing_target_days: number | null;
  over_sla_open_orders: number;
}
/** Revenue vs weight — the WHOLE report is Financials-gated (the server 403s a non-holder). */
export interface CrmProfitabilityReport {
  account_id: string;
  window_days: number | null;
  orders: number;
  revenue_total: number;
  currency: string | null;
  weight_kg: number;
  revenue_per_kg: number | null;
  invoices_issued: number;
}
/** Quarterly-business-review activity pack (Erp-readable — revenue deliberately excluded). */
export interface CrmQbrReport {
  account_id: string;
  period_start: string;
  period_end: string;
  orders: number;
  assets_processed: number;
  drives_sanitized: number;
  certificates_issued: number;
  weight_kg: number;
  sla: CrmSlaReport;
}

// pipe (Sites admin): a facility (site) list row — {id, name, active, certifications}. The Sites screen
// is admin-tier (a super-admin sees all, a pipe_admin its own). Management affordances are gated by the
// SERVER-provided provisioning flags (isSuperAdmin / isAdmin / my-facility ids), never re-derived from
// roles client-side; the API itself is the SSOT enforcement.
/** One facility certification — the exact jsonb shape the server's pickup guard consumes. */
export interface FacilityCertification {
  scheme: string;
  /** Optional scope note; the server omits the key entirely when unset. */
  scope?: string;
  /** ISO date (YYYY-MM-DD); the server treats a past date as lapsed (fail-closed pickup gate). */
  valid_through: string;
}
export interface FacilityRow {
  id: string;
  name: string;
  active: boolean;
  certifications: FacilityCertification[];
  // Ops/residency metadata. The PATCH/create REQUESTS accept these today, but the current
  // list/PATCH RESPONSES do not echo them — typed optional so the screen tolerates their absence
  // (prefill degrades to empty) and picks them up automatically once the server emits them.
  region?: string | null;
  country?: string | null;
  tz?: string | null;
  locale?: string | null;
  currency?: string | null;
}
// The optional site-metadata fields shared by facility create + PATCH. An absent field means
// unchanged (PATCH) / unset (create) — the callers OMIT an empty input, never send "".
export interface FacilityDetails {
  region?: string;
  country?: string;
  tz?: string;
  locale?: string;
  currency?: string;
}
// A facility member roster row. `is_admin` is best-effort from Keycloak (null when the directory is
// unconfigured or a per-member lookup failed — display only, never an authority signal).
export interface FacilityMember {
  user_id: string;
  email: string;
  is_admin: boolean | null;
  joined_at: string;
}
// A Keycloak directory user — the add-member picker shape.
export interface DirectoryUser {
  id: string;
  username: string;
  email: string;
}
/** The floor-station work surfaces (SSOT wire tokens — field-for-field with the Rust
 *  `rbac::station_key`). One token per station screen; the server publishes a boolean per token. */
export type StationKey = 'wipe' | 'qc' | 'dispatch' | 'outbound' | 'invoices';

// The post-login provisioning summary — also the SSOT for the Sites screen's affordance gating and
// the floor stations' nav gating: the actor's admin tier, station verdicts, and its facilities.
export interface ProvisioningInfo {
  userId?: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  /** 4B.6: the SERVER's staff-position verdict (the same `Field(StaffLocation)` gate the
   *  position_map report enforces — `pipe_manager` + the admin tiers, so BROADER than `isAdmin`).
   *  The position-map nav entry renders from this alone; never re-derive it from roles. Optional so
   *  a not-yet-redeployed server fails CLOSED (undefined ⇒ the entry stays hidden). */
  canViewStaffLocation?: boolean;
  /** Server verdict for the shared AI/agent access policy. Missing or failed = hidden. */
  canUseAiAgents?: boolean;
  /** Shared access policy AND the business-record read floor, evaluated on the server. */
  canAskPipe?: boolean;
  facilities: { id: string; name: string; workspaceId: string | null }[];
  /** The site-scope view map: ALL sites for a super admin (they may stand in any site's
   *  workspace), memberships otherwise. Separate from `facilities` on purpose — that array
   *  drives the workspace auto-mint flow, which stays membership-only (scale-safe). Optional so
   *  the client tolerates a not-yet-redeployed server (falls back to `facilities`). */
  viewFacilities?: { id: string; name: string; workspaceId: string | null }[];
  /** Task 0: the SERVER's per-station work verdict — `rbac::may_work_station`, composed from the
   *  very gates the matching write path runs (`authorize_erp_write` + `guard_lifecycle`'s stage
   *  pair, or `assign_pickup`'s `may_assign_driver`). A station's nav entry and its screen render
   *  from THIS alone; never re-derive a station from roles (4B.6 — a client that re-derives shows
   *  an action the server denies). Optional, and each member optional, so a not-yet-redeployed
   *  server fails CLOSED (absent ⇒ hidden) — read it through `stationGranted`, never directly. */
  stations?: Partial<Record<StationKey, boolean>>;
}

// 4B.6: the Ops Dashboard read-model DTOs — field-for-field with the Rust `OpsDashboardReport` /
// `StageMetric` (computed-never-stored, Erp-gated, facility-scoped; advisory, never blocks a
// transition). Numeric aggregates + non-sensitive stage/operator/role names only — no Entity.
export interface StageMetric {
  stage: string;
  owner_roles: string[];
  wip: number;
  weight_kg: number;
  dwell_days_avg: number | null;
  backlog: number;
  last_operator: string | null;
  goal_wip: number | null;
  goal_weight_kg: number | null;
}
export interface OpsDashboardReport {
  backlog_threshold_days: number;
  stages: StageMetric[];
}

// --- Work management (4H) DTOs ------------------------------------------------------------
// Field-for-field with the Rust `work.rs` read-model emissions (board multi-view, org chart,
// work dashboard). All are derived read-models: computed-never-stored, Erp-gated, facility-
// scoped server-side; every embedded item is an already-redacted entity projection (RecordRow).
/** One kanban bucket — `status: null` is the trailing "no/unknown status" bucket. */
export interface BoardViewBucket {
  status: string | null;
  items: RecordRow[];
}
/** One calendar day group (date = YYYY-MM-DD). */
export interface BoardViewDay {
  date: string;
  items: RecordRow[];
}
/** GET /api/work/boards/:id/view — the bounded board projection. `truncated` is the server's
 *  honest-bounds flag: when true the UI MUST show the localized truncation notice (never
 *  silently partial). Exactly one of items/buckets/(days+undated) is present per `mode`. */
export interface BoardView {
  board_id: string;
  mode: 'table' | 'kanban' | 'calendar';
  items?: RecordRow[];
  buckets?: BoardViewBucket[];
  days?: BoardViewDay[];
  undated?: RecordRow[];
  truncated: boolean;
}
/** Numeric work aggregates (per board / per assignee): counts only, never an Entity. */
export interface WorkAggregate {
  total: number;
  by_status: Record<string, number>;
  overdue: number;
}
export interface WorkBoardAggregate extends WorkAggregate {
  board_id: string;
  /** Scoped fail-soft board name — null when unresolvable (never a leaked label). */
  board_name: string | null;
}
export interface WorkAssigneeAggregate extends WorkAggregate {
  /** Null on the single fold-up row for unresolvable assignees (never a raw UUID). */
  assignee_id: string | null;
  display_name: string | null;
}
export interface WorkDashboardReport {
  boards: WorkBoardAggregate[];
  assignees: WorkAssigneeAggregate[];
}

// --- Director Brief ---------------------------------------------------------------------------
// Dedicated agreement-domain projections. Authority is represented only by the server action list.
export type DirectorAllowedAction =
  | 'acknowledge'
  | 'requestClarification'
  | 'update'
  | 'requestDecision'
  | 'recordDecision'
  | 'submitCompletion'
  | 'acceptCompletion'
  | 'requestRework'
  | 'cancel'
  | 'reassign'
  | 'follow'
  | 'unfollow'
  | 'clarify';
export type DirectorRole = 'orgDirector' | 'companyDirector' | 'companyMember';
export type DirectorInboxState = 'pending' | 'read' | 'acknowledged';
export type DirectorSourceInput =
  | { entityId: string }
  | { workspaceId: string; docId: string };
export type DirectorSourceRef =
  | {
      kind: 'entity';
      available: true;
      entityId: string;
      entityType: string;
      label: string | null;
    }
  | {
      kind: 'document';
      available: true;
      workspaceId: string;
      docId: string;
      label: string | null;
    }
  | { kind: 'unavailable'; available: false };
export interface DirectorCommitment {
  id: string;
  organizationId: string;
  companyId: string;
  title: string;
  outcome: string;
  successCriteria: string[];
  ownerId: string;
  delegatorId: string;
  reviewerId: string;
  deadline: string;
  acknowledgmentDeadline: string;
  nextUpdateAt: string;
  boundaries: string;
  decisionLimits: string;
  priority: 'critical' | 'high' | 'normal' | 'low';
  visibility: 'company' | 'participants';
  status:
    | 'pendingAcknowledgment'
    | 'clarificationRequested'
    | 'accepted'
    | 'decisionRequested'
    | 'completionSubmitted'
    | 'completed'
    | 'cancelled';
  parentCommitmentId: string | null;
  sourceRefs: DirectorSourceRef[];
  sourceCoverage: { total: number; available: number; unavailable: number };
  latestUpdate: {
    changed: string;
    nextStep: string;
    blocker: string | null;
    nextUpdateAt: string;
    createdAt: string;
  } | null;
  pendingDecision: {
    question: string;
    options: string[];
    neededBy: string;
    overdue: boolean;
    createdAt: string;
  } | null;
  acceptedUnderstanding: string | null;
  proposedDeadline: string | null;
  proposedAcknowledgmentDeadline: string | null;
  lastUpdateAt: string | null;
  freshness: 'missing' | 'fresh' | 'dueSoon' | 'overdue';
  deadlineState:
    | 'none'
    | 'onTrack'
    | 'dueSoon'
    | 'overdue'
    | 'completed'
    | 'cancelled';
  acknowledgmentState:
    | 'none'
    | 'onTrack'
    | 'dueSoon'
    | 'overdue'
    | 'completed'
    | 'cancelled';
  followed: boolean;
  allowedActions: DirectorAllowedAction[];
  version: number;
  createdAt: string;
  updatedAt: string;
}
export interface DirectorPage<T> {
  items: T[];
  nextCursor: string | null;
  truncated: boolean;
}
export interface DirectorContext {
  actorId: string;
  organizations: { id: string; name: string; role: DirectorRole }[];
  companies: {
    id: string;
    organizationId: string;
    key: string;
    name: string;
    role: DirectorRole;
    facilityIds: string[];
  }[];
  canProvision: boolean;
}
export interface DirectorOverview {
  asOf: string;
  organizationId: string;
  companyId: string | null;
  decisionRequests: DirectorPage<DirectorCommitment>;
  commitments: DirectorPage<DirectorCommitment> & {
    pendingAcknowledgment: number;
    overdueAcknowledgment: number;
    overdueDeadline: number;
    staleUpdate: number;
    completionSubmitted: number;
  };
  importantChanges: DirectorPage<DirectorImportantChange>;
  sourceSummaries: DirectorSourceSummaries;
}
export interface DirectorEvent {
  id: number;
  commitmentId: string;
  kind: string;
  actorId: string;
  summary: string;
  payload: Record<string, unknown>;
  createdAt: string;
}
export type DirectorImportantChange =
  | DirectorEvent
  | {
      id: number | string;
      kind: 'sourceChanged';
      action: string;
      sourceRef: Extract<DirectorSourceRef, { kind: 'entity' }>;
      changedAt: string;
    };
export interface DirectorDetail {
  commitment: DirectorCommitment;
  history: DirectorPage<DirectorEvent>;
}
/** Bounded, server-authorized names for delegation and reviewer pickers. */
export interface DirectorPerson {
  id: string;
  displayName: string | null;
  role: DirectorRole;
  companyId: string;
}
export interface DirectorInboxItem {
  id: string;
  eventId: number;
  kind: string;
  summary: string;
  commitmentId: string;
  state: DirectorInboxState;
  readAt: string | null;
  acknowledgedAt: string | null;
  version: number;
  createdAt: string;
  allowedActions: ('markRead' | 'acknowledge')[];
}
export interface DirectorOrganization {
  id: string;
  name: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}
export interface DirectorCompany {
  id: string;
  organizationId: string;
  key: string;
  name: string;
  facilityIds: string[];
  version: number;
  createdAt: string;
  updatedAt: string;
}
export interface DirectorGrant {
  id: string;
  organizationId: string;
  companyId: string | null;
  subjectId: string;
  displayName?: string | null;
  role: DirectorRole;
  version: number;
  revokedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface DirectorEligibleSubject {
  id: string;
  displayName: string | null;
  facilityIds: string[];
}
export interface DirectorFacility {
  id: string;
  name: string;
  active: boolean;
}
export interface DirectorSetup {
  organization: DirectorOrganization;
  companies: { items: DirectorCompany[]; truncated: boolean };
  grants: { items: DirectorGrant[]; truncated: boolean };
  eligibleSubjects: { items: DirectorEligibleSubject[]; truncated: boolean };
  facilities: { items: DirectorFacility[]; truncated: boolean };
}
export interface DirectorCreateInput {
  requestId: string;
  organizationId: string;
  companyId: string;
  title: string;
  outcome: string;
  successCriteria: string[];
  ownerId: string;
  reviewerId: string;
  deadline: string;
  acknowledgmentDeadline: string;
  nextUpdateAt: string;
  boundaries: string;
  decisionLimits: string;
  priority: DirectorCommitment['priority'];
  visibility: DirectorCommitment['visibility'];
  sourceRefs: DirectorSourceInput[];
  parentCommitmentId: string | null;
}
export type DirectorActionInput = {
  requestId: string;
  expectedVersion: number;
} & (
  | { action: 'acknowledge'; understanding: string }
  | {
      action: 'requestClarification';
      question: string;
      proposedDeadline: string | null;
      proposedAcknowledgmentDeadline: string | null;
    }
  | {
      action: 'clarify';
      answer: string;
      revisedDeadline: string | null;
      revisedAcknowledgmentDeadline: string | null;
      revisedNextUpdateAt: string | null;
      revisedSuccessCriteria: string[] | null;
    }
  | {
      action: 'update';
      changed: string;
      nextStep: string;
      blocker: string | null;
      nextUpdateAt: string;
    }
  | {
      action: 'requestDecision';
      question: string;
      options: string[];
      neededBy: string;
    }
  | { action: 'recordDecision'; decision: string; rationale: string }
  | {
      action: 'submitCompletion';
      summary: string;
      evidenceRefs: DirectorSourceInput[];
    }
  | { action: 'acceptCompletion'; reviewNote: string }
  | { action: 'requestRework'; reason: string; nextUpdateAt: string }
  | { action: 'cancel'; reason: string }
  | {
      action: 'reassign';
      newOwnerId: string;
      reason: string;
      acknowledgmentDeadline: string;
      nextUpdateAt: string;
    }
  | { action: 'follow' }
  | { action: 'unfollow' }
);
/** One org-chart node — the non-sensitive directory spine projection. */
export interface OrgChartNode {
  id: string;
  display_name: string | null;
  department: string | null;
  title: string | null;
}
/** One reports-to edge: `source` REPORTS TO `target`. */
export interface OrgChartEdge {
  source: string;
  target: string;
}
/** GET /api/work/org-chart/:root — server-bounded (depth 64 / 500 nodes). */
export interface OrgChart {
  root: string;
  nodes: OrgChartNode[];
  edges: OrgChartEdge[];
}

// --- QMS (4F) DTOs -------------------------------------------------------------------------
// Field-for-field with the Rust `qms.rs` `QmsDashboardReport` / `TrainingMatrixReport` —
// counts + non-sensitive enum labels only (computed-never-stored, Erp-gated, facility-scoped).
export interface QmsDashboardReport {
  open_ncrs_by_source: [string, number][];
  open_ncrs_by_severity: [string, number][];
  overdue_capa: number;
  docs_due_review: number;
  docs_past_retention: number;
  training_gaps: number;
  calibration_expired: number;
  vendors_expiring_60d: number;
}
/** One training-matrix course column (non-sensitive name/competency). */
export interface TrainingCourseCol {
  id: string;
  name: string;
  competency: string | null;
}
/** One employee row: fail-soft display name + one status cell per course column (aligned). */
export interface TrainingMatrixRow {
  employee_id: string;
  display_name: string | null;
  cells: string[];
}
export interface TrainingMatrixReport {
  courses: TrainingCourseCol[];
  rows: TrainingMatrixRow[];
}

/** One portal invite row (the internal list — the token is NEVER echoed here). */
export interface PortalInviteRow {
  id: string;
  email: string;
  display_name: string | null;
  created_at: string;
  expires_at: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
}

/** The create response — `token` is shown ONCE (the server keeps only its hash); the UI composes
 *  `<origin>/activate?token=…` from the server-SSOT `origins` list. */
export interface PortalInviteCreated {
  invite_id: string;
  expires_at: string;
  token: string;
  origins: string[];
}

// The ONE fetch core every helper shares (SSOT — same credentials/headers/error shape). A non-ok
// response throws `ErpApiError` (an Error subclass with the same message format every catch-site
// already renders, PLUS the status/detail the status-branching screens read).
type ErpRequestInit = RequestInit & { readOnly?: boolean };
async function request(path: string, init?: ErpRequestInit): Promise<Response> {
  const { readOnly = false, ...options } = init ?? {};
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: {
      accept: 'application/json',
      // Site-scope view narrowing — server-validated, narrow-only (see erp-site-scope.ts).
      ...siteScopeHeaders(),
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
    },
    ...options,
  });
  if (!res.ok) {
    throw await toErpApiError(res, init?.method ?? 'GET', path);
  }
  if (
    !readOnly &&
    init?.method &&
    !['GET', 'HEAD', 'OPTIONS'].includes(init.method.toUpperCase())
  ) {
    requestErpRefresh();
  }
  return res;
}

async function api<T>(path: string, init?: ErpRequestInit): Promise<T> {
  return (await (await request(path, init)).json()) as T;
}

/** How many rows a station work-queue asks for. Declared ONCE (SSOT) so no station view compares
 *  `rows.length` against a hand-copied magic number — the honest cap verdict comes from the server's
 *  `x-list-truncated` header, and this is only the page size we request. */
export const STATION_QUEUE_LIMIT = 200;

/** The server's page-cap header — the SSOT wire name (`entity::LIST_TRUNCATED_HEADER`). */
const LIST_TRUNCATED_HEADER = 'x-list-truncated';

/** Like `api()`, but also reads the list-truncation header. The BODY is unchanged (a bare JSON
 *  array), so every existing caller is untouched — the flag rides beside it. */
async function apiPage<T>(
  path: string,
  init?: RequestInit
): Promise<{ body: T; truncated: boolean }> {
  const res = await request(path, init);
  const truncated = res.headers.get(LIST_TRUNCATED_HEADER) === 'true';
  return { body: (await res.json()) as T, truncated };
}

// A no-body variant for 204 responses (DELETE member) — the JSON `api()` throws on an empty body.
async function apiNoContent(path: string, init?: RequestInit): Promise<void> {
  await request(path, init);
}

/** Last verified site map. This is display narrowing, never an authorization grant. */
export type ErpSiteMapping = Pick<
  ProvisioningInfo,
  'userId' | 'facilities' | 'viewFacilities'
>;

export class ErpService extends Service {
  /** The current actor's provisioning summary (admin tier + facility memberships), shared and
   *  revalidated here so the Sites screen AND its admin-gated sidebar nav read ONE source (SSOT/DRY). The
   *  Core API `/me/provisioning` is the authority; this never re-derives admin status client-side.
   *  `null` = not-yet-loaded OR a load failure (fail-closed — callers treat null as "no admin"). */
  readonly provisioning$ = new LiveData<ProvisioningInfo | null>(null);
  readonly siteMapping$ = new LiveData<ErpSiteMapping | null>(null);
  readonly provisioningStatus$ = new LiveData<'loading' | 'ready' | 'error'>(
    'loading'
  );
  private provisioningPromise: Promise<ProvisioningInfo> | null = null;
  /** The one-shot cached provisioning fetch — the SSOT for the sidebar gate, the Sites screen,
   *  AND the post-login facility-workspace bootstrap (one network round-trip per session, three
   *  consumers). A rejection clears the cache so a later call retries — a provisioning error
   *  must NEVER reveal admin-only UI (provisioning$ stays null, fail-closed). */
  provisioningOnce(): Promise<ProvisioningInfo> {
    return this.provisioningPromise ?? this.provisioningFresh();
  }
  ensureProvisioning(): void {
    this.provisioningOnce().catch(() => {});
  }
  provisioningFresh(): Promise<ProvisioningInfo> {
    this.provisioningStatus$.next('loading');
    const p = this.provisioning().then(info => {
      if (this.provisioningPromise === p) {
        this.siteMapping$.next({
          userId: info.userId,
          facilities: info.facilities,
          viewFacilities: info.viewFacilities,
        });
        this.provisioning$.next(info);
        this.provisioningStatus$.next('ready');
      }
      return info;
    });
    this.provisioningPromise = p;
    p.catch(() => {
      if (this.provisioningPromise === p) {
        this.provisioningPromise = null;
        this.provisioning$.next(null);
        this.provisioningStatus$.next('error');
      }
    });
    return p;
  }

  private provisioningObservers = 0;
  private provisioningTimer: ReturnType<typeof setInterval> | null = null;
  private automaticProvisioning: Promise<ProvisioningInfo> | null = null;
  private readonly refreshProvisioning = () => {
    if (document.visibilityState !== 'visible' || this.automaticProvisioning)
      return;
    // Revalidate in place. A pending request is not a denial or a change of site.
    // A failed check still clears authority in provisioningFresh; every API action
    // independently checks current server permissions.
    const pending = this.provisioningFresh();
    this.automaticProvisioning = pending;
    pending
      .catch(() => {})
      .finally(() => {
        if (this.automaticProvisioning === pending)
          this.automaticProvisioning = null;
      });
  };
  observeProvisioning(): () => void {
    if (this.provisioningObservers++ === 0) {
      this.ensureProvisioning();
      this.provisioningTimer = setInterval(this.refreshProvisioning, 30_000);
      window.addEventListener('focus', this.refreshProvisioning);
      window.addEventListener('online', this.refreshProvisioning);
      document.addEventListener('visibilitychange', this.refreshProvisioning);
    }
    return () => {
      if (--this.provisioningObservers === 0) {
        if (this.provisioningTimer) clearInterval(this.provisioningTimer);
        this.provisioningTimer = null;
        window.removeEventListener('focus', this.refreshProvisioning);
        window.removeEventListener('online', this.refreshProvisioning);
        document.removeEventListener(
          'visibilitychange',
          this.refreshProvisioning
        );
        this.automaticProvisioning = null;
        this.provisioningPromise = null;
        this.provisioning$.next(null);
      }
    };
  }

  mondayStatus(): Promise<MondayStatus> {
    return api('/monday/status');
  }
  mondaySave(config: MondayConfig): Promise<MondayStatus> {
    return api('/monday/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }
  mondayRun(action: 'scan' | 'process' | 'retry'): Promise<MondayStatus> {
    return api(`/monday/${action}`, { method: 'POST', body: '{}' });
  }
  mondayItems(board: string, after?: string): Promise<MondayItemPage> {
    const params = new URLSearchParams({ board_id: board });
    if (after) params.set('after', after);
    return api(`/monday/items?${params}`);
  }
  mondayLinkCandidates(
    board: string,
    item: string,
    search: string
  ): Promise<{
    items: { id: string; title: string | null; order_no: string | null }[];
    truncated: boolean;
  }> {
    return api(
      `/monday/items/${encodeURIComponent(board)}/${encodeURIComponent(item)}/link-candidates?${new URLSearchParams({ search })}`
    );
  }
  mondayLink(
    board: string,
    item: string,
    entity_id: string
  ): Promise<MondaySourceDetail> {
    return api(
      `/monday/items/${encodeURIComponent(board)}/${encodeURIComponent(item)}/link`,
      { method: 'POST', body: JSON.stringify({ entity_id }) }
    );
  }
  mondaySource(boardId: string, itemId: string): Promise<MondaySourceDetail> {
    return api(
      `/monday/items/${encodeURIComponent(boardId)}/${encodeURIComponent(itemId)}`
    );
  }

  // --- Hop-list SWR cache (perf round 2) ------------------------------------------------------
  // Serve-cached-then-revalidate for hop-heavy screens (the comms tabs, the work directory,
  // picker sources): a remount renders the last-known rows INSTANTLY while a background fetch
  // refreshes them (the caller applies latest-wins). The cache holds only already-redacted rows
  // THIS session/actor fetched — a stale-then-fresh render, never stale-only, never cross-actor.
  // The per-key seq is latest-wins for the CACHE itself: only the newest in-flight fetch may
  // write it, and a mutation write-through (`swrUpdate`) bumps the seq so an older in-flight
  // snapshot can never clobber the mutated value.
  private readonly swrCache = new Map<string, unknown>();
  private readonly swrSeq = new Map<string, number>();
  private swr<T>(
    key: string,
    fetcher: () => Promise<T>
  ): { cached?: T; fresh: Promise<T> } {
    // Scope-local cache identity: a row list cached under one site view must never instant-paint
    // into another (the scope is part of what the key MEANS — review fix).
    key = `${scopeCacheKey()}|${key}`;
    const cached = this.swrCache.get(key) as T | undefined;
    const seq = (this.swrSeq.get(key) ?? 0) + 1;
    this.swrSeq.set(key, seq);
    const fresh = fetcher().then(value => {
      if (this.swrSeq.get(key) === seq) this.swrCache.set(key, value);
      return value;
    });
    return { cached, fresh };
  }
  /** Write a mutation through to a cached list (and invalidate in-flight cache writes), so the
   *  next visit's instant paint shows the mutated row — never a resurrected pre-mutation one.
   *  `key` must already carry its scope prefix, CAPTURED AT MUTATION ISSUE TIME: a scope flip
   *  while the mutation is in flight must invalidate the ORIGIN scope's cache (the one the
   *  request's header was sent under), never the new scope's (site-scope audit fix). */
  private swrUpdateAt<T>(key: string, updater: (value: T) => T): void {
    this.swrSeq.set(key, (this.swrSeq.get(key) ?? 0) + 1);
    const cur = this.swrCache.get(key) as T | undefined;
    if (cur !== undefined) this.swrCache.set(key, updater(cur));
  }
  /** The comms feed's retry write-through (see `messages.tsx` onRetry): returns an updater BOUND
   *  to the current scope — obtain it BEFORE issuing the mutation. */
  swrMessagesWriteThrough(): (
    updater: (rows: CommsRow[]) => CommsRow[]
  ) => void {
    const key = `${scopeCacheKey()}|comms:messages`;
    return updater => this.swrUpdateAt(key, updater);
  }
  swrNotifications() {
    return this.swr('comms:notifications', () => this.listNotifications());
  }
  swrMessages() {
    return this.swr('comms:messages', () => this.listMessages());
  }
  swrSubscriptions() {
    return this.swr('comms:subscriptions', () => this.listSubscriptions());
  }
  swrWorkDirectory() {
    return this.swr('work:directory', () => this.workDirectory());
  }
  /** Picker sources (contact/email_template/lead/campaign rows for Menu pick lists). */
  swrRecordsPicker(type: string) {
    return this.swr(`picker:${type}`, () => this.listRecords(type));
  }

  // Entity schemas are static registry data per session, but every screen mount used to refetch
  // them (~12 call sites). Session-scoped promise caches (the `ensureProvisioning` pattern):
  // served from memory after the first fetch; a REJECTED promise is evicted so the next mount
  // retries (fail-soft callers keep their existing .catch behavior). Schemas are not per-actor
  // (the registry declares shape + sensitivity flags globally), so caching leaks nothing.
  private readonly schemaCache = new Map<string, Promise<EntitySchema>>();
  private schemasPromise: Promise<EntitySchema[]> | null = null;

  listSchemas(): Promise<EntitySchema[]> {
    if (this.schemasPromise) return this.schemasPromise;
    const p = api<EntitySchema[]>('/entity-schemas');
    this.schemasPromise = p;
    p.then(schemas => {
      // The registry list carries every per-type schema — pre-warm the per-type cache.
      for (const s of schemas) {
        this.schemaCache.set(s.type_name, Promise.resolve(s));
      }
    }).catch(() => {
      this.schemasPromise = null;
    });
    return p;
  }
  schemaFor(type: string): Promise<EntitySchema> {
    const cached = this.schemaCache.get(type);
    if (cached) return cached;
    const p = api<EntitySchema>(`/entity-schemas/${encodeURIComponent(type)}`);
    this.schemaCache.set(type, p);
    p.catch(() => {
      // Evict only if this failed promise is still the cached entry — a listSchemas() that
      // resolved meanwhile may have pre-warmed a good one.
      if (this.schemaCache.get(type) === p) this.schemaCache.delete(type);
    });
    return p;
  }
  /** One page of a list read PLUS the server's exact "there is more" flag (`x-list-truncated`).
   *  A grid renders ~200 rows of a type that can hold 250k, so a page shown with no signal presents
   *  a fraction as the whole set — the no-silent-partial rule (the same convention as
   *  `x-scan-truncated` / `x-export-truncated`). */
  listRecordsPage(
    type: string,
    opts?: Parameters<ErpService['listRecords']>[1]
  ): Promise<RecordPage> {
    return this.page<RecordRow>(this.recordsPath(type, opts));
  }
  /** One page reader for EVERY list surface: the generic `/records` core above and the fixed-kind
   *  routes below (`/sales/*`, `/work/*`, `/listings`), which now carry the same `x-list-truncated`
   *  verdict from the same server helper. One convention, one client seam. */
  private page<T>(path: string): Promise<RecordPage<T>> {
    return apiPage<T[]>(path).then(({ body, truncated }) => ({
      rows: body,
      truncated,
    }));
  }
  private recordsPath(
    type: string,
    opts?: Parameters<ErpService['listRecords']>[1]
  ): string {
    const params = new URLSearchParams({
      type,
      limit: String(opts?.limit ?? 200),
    });
    if (opts?.account) params.set('account', opts.account);
    if (opts?.order) params.set('order', opts.order);
    if (opts?.hideDuplicates) params.set('hide_duplicates', 'true');
    return `/records?${params}`;
  }
  listRecords(
    type: string,
    opts?: {
      /** Server-side per-customer narrowing (ROMS-scale fix — client filtering over a global
       *  first-page made big customers invisible). */
      account?: string;
      /** Server-side per-job narrowing (order detail — rows tagged `inbound_order_id`). */
      order?: string;
      limit?: number;
      /** Hide drive rows marked as duplicate registrations (`duplicate_of_drive_id` set) —
       *  SERVER-side, so the page and its truncated verdict stay honest (never a client-only
       *  narrowing over a capped window). */
      hideDuplicates?: boolean;
    }
  ): Promise<RecordRow[]> {
    return api(this.recordsPath(type, opts));
  }
  /** 4B.6: the work-queue at one operations stage (server-SSOT actionability per row), PLUS the
   *  server's exact "there is more than fits this page" verdict (`x-list-truncated`, computed from a
   *  probe row the server drops before it audits or emits anything). A floor queue that stops at 200
   *  with no signal presents a fraction as the whole set. Hits the cookie route /api/ops/worklist. */
  listWorklistPage(
    stage: string,
    limit = STATION_QUEUE_LIMIT
  ): Promise<RecordPage> {
    return this.pageOf(
      `/ops/worklist?stage=${encodeURIComponent(stage)}&limit=${limit}`
    );
  }
  /** The rows alone — the pre-existing shape, kept byte-identical for the stage Worklist screen. */
  listWorklist(stage: string): Promise<RecordRow[]> {
    return this.listWorklistPage(stage).then(p => p.rows);
  }
  /** The ONE place a list route's body + `x-list-truncated` header become a `RecordPage` (DRY —
   *  every station queue reads its cap verdict through this, never by guessing from `rows.length`). */
  private pageOf(path: string, init?: RequestInit): Promise<RecordPage> {
    return apiPage<RecordRow[]>(path, init).then(({ body, truncated }) => ({
      rows: body,
      truncated,
    }));
  }
  /** 4B.6: the per-stage Ops Dashboard aggregates (computed, never stored; never blocks). Hits the
   *  cookie route /api/ops/dashboard (the `api()` helper prefixes /api). */
  opsDashboard(): Promise<OpsDashboardReport> {
    return api<OpsDashboardReport>('/ops/dashboard');
  }
  /** P1.5: one order's derived SLA/deliverable clocks (computed server-side, never stored;
   *  advisory). Hits the cookie route /api/ops/orders/:id/sla. */
  orderSla(orderId: string): Promise<OrderSlaReport> {
    return api<OrderSlaReport>(
      `/ops/orders/${encodeURIComponent(orderId)}/sla`
    );
  }
  /** 4B.9: the staff Customers list — own-customers-only by default, all for managers/admins (the
   *  SERVER narrows). Each row carries the server-computed `owner_manageable` flag the UI gates the
   *  assign/transfer/revoke affordance on. PLUS the server's exact "there is more than fits this
   *  page" verdict (`x-list-truncated`) — the account book crosses the server's 10,000-row ceiling
   *  as more sites onboard, and a page rendered with no signal reads as the whole book (F061).
   *  Hits the cookie route /api/accounts. */
  listAccountsPage(): Promise<RecordPage<AccountRow>> {
    return apiPage<AccountRow[]>('/accounts').then(({ body, truncated }) => ({
      rows: body,
      truncated,
    }));
  }
  // --- Customer 360: portal access (2026-08-25 invite flow — copy-link; email is off). Server
  // authority: config-gated 404 when invites are off, Erp write + may_assign_account gates, scoped
  // account visibility. The create response carries the one-time token EXACTLY ONCE.
  listPortalInvites(
    accountId: string
  ): Promise<{ invites: PortalInviteRow[] }> {
    return api<{ invites: PortalInviteRow[] }>(
      `/accounts/${encodeURIComponent(accountId)}/portal-invites`
    );
  }
  createPortalInvite(
    accountId: string,
    body: { email: string; display_name?: string }
  ): Promise<PortalInviteCreated> {
    return api<PortalInviteCreated>(
      `/accounts/${encodeURIComponent(accountId)}/portal-invites`,
      { method: 'POST', body: JSON.stringify(body) }
    );
  }
  /** Pull an in-flight invite before its TTL expires (F127) — same account-assignment authority as
   *  `createPortalInvite` (`may_assign_account`), so no separate per-row flag is needed: the
   *  affordance rides the section's existing visibility gate. An already-accepted/expired/foreign
   *  invite is a flat 404 from the server, never an oracle. */
  revokePortalInvite(accountId: string, inviteId: string): Promise<void> {
    return apiNoContent(
      `/accounts/${encodeURIComponent(accountId)}/portal-invites/${encodeURIComponent(inviteId)}/revoke`,
      { method: 'POST' }
    );
  }
  // --- Customer 360: the 4C.2 CRM read-models (cookie twins of the bearer /crm/accounts/:id/*;
  // all authority lives server-side in `crm.rs` — Erp-gated, account-visibility-checked, scoped).
  /** SLA scorecard (Erp-readable). */
  crmSla(accountId: string): Promise<CrmSlaReport> {
    return api<CrmSlaReport>(
      `/crm/accounts/${encodeURIComponent(accountId)}/sla`
    );
  }
  /** Profitability — Financials-gated: a non-holder gets a 403 `ErpApiError` (render nothing, never
   *  a zeroed report). */
  crmProfitability(accountId: string): Promise<CrmProfitabilityReport> {
    return api<CrmProfitabilityReport>(
      `/crm/accounts/${encodeURIComponent(accountId)}/profitability`
    );
  }
  /** QBR activity pack (Erp-readable; default period = the last quarter). */
  crmQbr(accountId: string): Promise<CrmQbrReport> {
    return api<CrmQbrReport>(
      `/crm/accounts/${encodeURIComponent(accountId)}/qbr`
    );
  }
  /** Documents slice: upload a file onto a customer account (raw-bytes body; the server coerces
   *  active MIME types, computes sha256, and mints the audited `customer_document` row through the
   *  one create SSOT). Rejects with `ErpApiError` (403 for an agent PAT, 404 for a foreign account). */
  async uploadAccountDocument(
    accountId: string,
    file: File,
    opts?: {
      docKind?: string;
      title?: string;
      /** Link to a job — must be a visible inbound_order of THIS account (server 409s a foreign one). */
      orderId?: string;
      /** Date of the service the document evidences (RFC3339). */
      documentDate?: string;
      /** The paper's own job/reference number (free text — e.g. "EWG06765"). */
      jobReference?: string;
      /** IP7.7: the ITEM this paper evidences (the passport subject's id) — rides the same query
       *  wire as the other metadata (`item_ref`); the server validates and stores it. */
      itemRef?: string;
    }
  ): Promise<RecordRow> {
    const params = new URLSearchParams({
      file_name: file.name,
      title: opts?.title || file.name,
    });
    if (opts?.docKind) params.set('doc_kind', opts.docKind);
    if (opts?.orderId) params.set('inbound_order_id', opts.orderId);
    if (opts?.documentDate) params.set('document_date', opts.documentDate);
    if (opts?.jobReference) params.set('job_reference', opts.jobReference);
    if (opts?.itemRef) params.set('item_ref', opts.itemRef);
    const path = `/accounts/${encodeURIComponent(accountId)}/documents?${params}`;
    const res = await fetch(`/api${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        ...siteScopeHeaders(),
        'content-type': file.type || 'application/octet-stream',
      },
      body: file,
    });
    if (!res.ok) {
      throw await toErpApiError(res, 'POST', path);
    }
    return (await res.json()) as RecordRow;
  }
  /** The in-app viewer/download URL for a customer document (cookie-authed; served with the
   *  server's inline-allowlist MIME gate). */
  documentFileUrl(id: string): string {
    return `/api/documents/${encodeURIComponent(id)}/file`;
  }
  /** P0.7: issue a signed Certificate of Destruction for an order. The server re-checks the full
   *  authority set (Erp write + DataDestruction) — the button only renders where the order row's
   *  `can_issue_certificates` affordance is true. Rejects with `ErpApiError` (403/404). */
  issueOrderCertificate(orderId: string): Promise<CertificateIssueResult> {
    return api<CertificateIssueResult>(
      `/ops/orders/${encodeURIComponent(orderId)}/certificate`,
      { method: 'POST' }
    );
  }
  /** P0.7: issue a signed Certificate of Recycling for an order (same contract as the CoD). */
  issueRecyclingCertificate(orderId: string): Promise<CertificateIssueResult> {
    return api<CertificateIssueResult>(
      `/ops/orders/${encodeURIComponent(orderId)}/recycling-certificate`,
      { method: 'POST' }
    );
  }
  /** P0.6: the cookie-authed printable Bill of Lading PDF URL (opens inline). */
  bolPdfUrl(id: string): string {
    return `/api/ops/bol/${encodeURIComponent(id)}/pdf`;
  }
  /** Internal staff download of an issued Certificate of Destruction PDF (cookie-authed; the server
   *  re-checks Erp + DataDestruction + facility scope). `certId` is the destruction_certificate entity
   *  id (the `id` on a CertificateIssueResult or a loaded cert row). `?bundle=1` streams the signed
   *  evidence ZIP instead. */
  certDownloadUrl(certId: string): string {
    return `/api/ops/certificates/${encodeURIComponent(certId)}/pdf`;
  }
  /** P3.5: the cookie-authed printable ops-document PDF URL for an order (opens inline). The
   *  OSDW/OSDS buttons render only where the order row's `can_print_sanitization_docs` is true;
   *  the server re-checks regardless. */
  opsDocUrl(orderId: string, kind: 'osdw' | 'arr' | 'osaf' | 'osds'): string {
    return `/api/ops/orders/${encodeURIComponent(orderId)}/documents/${kind}`;
  }
  /** Digital drop-off release: the customer's on-screen sign-off for an order. The server renders
   *  the release PDF (statement + items + drawn signature) and files it as a job-linked
   *  `customer_document` — returns that row. Rejects with `ErpApiError` (409 customer-less order). */
  signRelease(
    orderId: string,
    sig: {
      signerName: string;
      strokes: [number, number][][];
      canvasWidth: number;
      canvasHeight: number;
    }
  ): Promise<RecordRow> {
    return api<RecordRow>(`/orders/${encodeURIComponent(orderId)}/release`, {
      method: 'POST',
      body: JSON.stringify({
        signer_name: sig.signerName,
        strokes: sig.strokes,
        canvas_width: sig.canvasWidth,
        canvas_height: sig.canvasHeight,
      }),
    });
  }
  /** 4C: global cross-entity search — POSTs the cookie route /api/search (the browser counterpart of
   *  the bearer /search; both delegate to the one `search_core` chokepoint). The server enforces the
   *  Erp gate + facility/account partition + redaction-at-WHERE, so every returned row is already
   *  redacted (sensitive keys stripped, sensitive titles masked) — client filtering over these is
   *  never a security boundary. Default mode `keyword` (substring); `semantic` needs the provisioned
   *  embedder (the self-hosted-inference gate), so it is opt-in.
   *
   *  `types` (list findability) narrows the search to the registry type(s) a screen actually lists.
   *  It is an ADDITIONAL `AND entity.type = ANY(...)` on the SAME server query — the partition, the
   *  restricted-type exclusion and the redaction-at-WHERE are untouched, so it can only REMOVE rows,
   *  never widen. Absent / empty ⇒ today's all-types search, byte-identical. */
  search(
    query: string,
    opts?: { mode?: SearchMode; limit?: number; types?: readonly string[] }
  ): Promise<RecordRow[]> {
    const types = opts?.types;
    return api<RecordRow[]>('/search', {
      readOnly: true,
      method: 'POST',
      body: JSON.stringify({
        query,
        mode: opts?.mode ?? 'keyword',
        limit: opts?.limit ?? 30,
        ...(types && types.length > 0 ? { types: [...types] } : {}),
      }),
    });
  }
  /** Ranked, paginated discovery; UI and MCP share record_search::search_records_core. */
  searchRecords(
    query: string,
    opts?: {
      limit?: number;
      offset?: number;
      types?: readonly string[];
      filters?: { order?: string; account?: string };
      signal?: AbortSignal;
    }
  ): Promise<RecordSearchPage> {
    return api<RecordSearchPage>('/search/records', {
      readOnly: true,
      method: 'POST',
      signal: opts?.signal,
      body: JSON.stringify({
        query,
        limit: opts?.limit ?? 30,
        offset: opts?.offset ?? 0,
        ...(opts?.types?.length ? { types: opts.types } : {}),
        ...(opts?.filters ? { filters: opts.filters } : {}),
      }),
    });
  }
  /** Scan-intake lookup: resolve a scanned barcode / serial to its entity rows (cookie GET
   *  /api/entities/scan/:code). The server is the authority — Erp-gated + facility-scoped +
   *  the ONE redaction serializer, so every returned row is already redacted; each row's
   *  `type`/`stage` drive the client's type→route mapping (erpResultRoute). */
  scanLookup(code: string): Promise<RecordRow[]> {
    return api<RecordRow[]>(`/entities/scan/${encodeURIComponent(code)}`);
  }
  getRecord(id: string): Promise<RecordRow> {
    return api(`/entities/${encodeURIComponent(id)}`);
  }
  /** `facilityId` (optional, scan-intake): pins the created row to a site via the TOP-LEVEL
   *  `facility_id` column of the create body — the server refuses a site the actor doesn't
   *  belong to and write paths deliberately ignore the narrow-only view header, so an intake
   *  create MUST carry it explicitly (absent = company-wide NULL, unchanged for all existing
   *  callers). */
  createRecord(
    type: string,
    title: string | null,
    data: Record<string, unknown>,
    facilityId?: string,
    /** `mintTag` (intake, asset only): ask the server to mint the next ROMS-series asset tag into
     *  `data.tag_id` inside the create tx (top-level `mint_tag` flag — the server refuses it on any
     *  other type or alongside an explicit tag_id). */
    opts?: { mintTag?: boolean }
  ): Promise<RecordRow> {
    return api('/entities', {
      method: 'POST',
      body: JSON.stringify({
        type,
        title,
        data,
        ...(facilityId ? { facility_id: facilityId } : {}),
        ...(opts?.mintTag ? { mint_tag: true } : {}),
      }),
    });
  }
  updateRecord(
    id: string,
    title: string | null,
    data: Record<string, unknown>
  ): Promise<RecordRow> {
    return api(`/entities/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ title, data }),
    });
  }
  /** IP7.2: link two visible records through the cookie POST /api/relations — the session twin
   *  of the bearer link route (the ONE `link_core` chokepoint: Erp-write-gated, both endpoints
   *  visibility-checked under the partition, `REPORTS_TO` reserved, the data-bearing link guard
   *  refuses with an explicit message). The response carries no body (a bare status). */
  createRelation(src: string, dst: string, rel: string): Promise<void> {
    return apiNoContent('/relations', {
      method: 'POST',
      body: JSON.stringify({ src, dst, rel }),
    });
  }
  transition(
    id: string,
    to: string,
    reason?: string,
    disposition?: string
  ): Promise<RecordRow> {
    return api(`/entities/${encodeURIComponent(id)}/transition`, {
      method: 'POST',
      body: JSON.stringify({
        to,
        ...(reason ? { reason } : {}),
        ...(disposition ? { disposition } : {}),
      }),
    });
  }

  // --- Sites admin (facilities) -------------------------------------------------------------
  /** The Sites list (admin-tier; super-admin sees all, a pipe_admin its own). Cookie route /api/facilities. */
  listFacilities(): Promise<FacilityRow[]> {
    return api<FacilityRow[]>('/facilities');
  }
  /** Create a site (super-admin only — the server enforces; the UI offers it only to a super-admin). */
  createFacility(
    name: string,
    details?: FacilityDetails
  ): Promise<FacilityRow> {
    return api<FacilityRow>('/facilities', {
      method: 'POST',
      body: JSON.stringify({ name, ...details }),
    });
  }
  /**
   * Edit a site: name/tz/locale/currency (admin of it) / `active` retire-reopen + residency +
   * certifications (super-only). `certifications` provided ⇒ REPLACES the whole stored array (empty ⇒
   * clears; the pickup gate then fail-closes); absent ⇒ unchanged. Server-gated + validated regardless.
   */
  updateFacility(
    id: string,
    patch: FacilityDetails & {
      name?: string;
      active?: boolean;
      certifications?: FacilityCertification[];
    }
  ): Promise<FacilityRow> {
    return api<FacilityRow>(`/facilities/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }
  /** A site's member roster (email + best-effort KC tier). Cookie route /api/facilities/:id/members. */
  listFacilityMembers(id: string): Promise<FacilityMember[]> {
    return api<FacilityMember[]>(
      `/facilities/${encodeURIComponent(id)}/members`
    );
  }
  /** Add a member (Option B: a site-admin may add only a NON-admin; the server vets + enforces). */
  addFacilityMember(id: string, userId: string): Promise<unknown> {
    return api(`/facilities/${encodeURIComponent(id)}/members`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
  }
  /** Remove a member (Option-B-symmetric; server-gated). 204 → no body. */
  removeFacilityMember(id: string, userId: string): Promise<void> {
    return apiNoContent(
      `/facilities/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );
  }
  /** Leaver-cutoff (owner decision 2026-09-06): immediately end every one of `userId`'s sessions, on
   *  every device. Admin-tier + facility-scoped server-side (`facility::may_revoke_sessions`) —
   *  offered here only where `membersManageable`, but the API independently re-checks regardless. */
  revokeUserSessions(userId: string): Promise<{ sessions_revoked: number }> {
    return api<{ sessions_revoked: number }>(
      `/admin/users/${encodeURIComponent(userId)}/revoke-sessions`,
      { method: 'POST' }
    );
  }
  /** The add-member picker — read-only Keycloak directory search (admin-tier; q >= 2, capped server-side). */
  searchDirectory(query: string): Promise<DirectoryUser[]> {
    return api<DirectoryUser[]>(
      `/admin/users/search?q=${encodeURIComponent(query)}`
    );
  }
  /** The actor's admin tier + facility memberships — the SSOT for the Sites screen's affordance gating. */
  provisioning(): Promise<ProvisioningInfo> {
    return api<ProvisioningInfo>('/me/provisioning');
  }

  // --- Work management (4H) -----------------------------------------------------------------
  // Cookie routes /api/work/* (work.rs cookie_routes). Creates go through the /work surface so
  // the server derives the search-reachable title from each type's human identifier field;
  // updates + transitions reuse the generic updateRecord/transition (the SAME entity handlers
  // are mounted on both surfaces — one chokepoint, no second write path).
  /** The work-boards list (Erp-gated + facility-scoped + redacted server-side). */
  listWorkBoards(): Promise<RecordRow[]> {
    return api<RecordRow[]>('/work/boards');
  }
  createWorkBoard(
    title: string | null,
    data: Record<string, unknown>
  ): Promise<RecordRow> {
    return api('/work/boards', {
      method: 'POST',
      body: JSON.stringify({ title, data }),
    });
  }
  /** The bounded board projection (table|kanban|calendar). `truncated` must surface in the UI. */
  boardView(
    id: string,
    mode: 'table' | 'kanban' | 'calendar'
  ): Promise<BoardView> {
    return api<BoardView>(
      `/work/boards/${encodeURIComponent(id)}/view?mode=${mode}`
    );
  }
  /** The work-items list, optionally filtered to one board (a PUBLIC token filter). */
  listWorkItems(boardId?: string): Promise<RecordRow[]> {
    return api<RecordRow[]>(
      `/work/items${boardId ? `?board_id=${encodeURIComponent(boardId)}` : ''}`
    );
  }
  createWorkItem(
    title: string | null,
    data: Record<string, unknown>
  ): Promise<RecordRow> {
    return api('/work/items', {
      method: 'POST',
      body: JSON.stringify({ title, data }),
    });
  }
  /** Customer feedback (a sensitive type — rows arrive pre-masked by the ONE serializer). */
  listWorkFeedback(): Promise<RecordRow[]> {
    return api<RecordRow[]>('/work/feedback');
  }
  createWorkFeedback(
    title: string | null,
    data: Record<string, unknown>
  ): Promise<RecordRow> {
    return api('/work/feedback', {
      method: 'POST',
      body: JSON.stringify({ title, data }),
    });
  }
  listWorkRequests(): Promise<RecordRow[]> {
    return api<RecordRow[]>('/work/requests');
  }
  createWorkRequest(
    title: string | null,
    data: Record<string, unknown>
  ): Promise<RecordRow> {
    return api('/work/requests', {
      method: 'POST',
      body: JSON.stringify({ title, data }),
    });
  }
  listWorkFormsPage(): Promise<RecordPage> {
    return this.page<RecordRow>('/work/forms');
  }
  createWorkForm(
    title: string | null,
    data: Record<string, unknown>
  ): Promise<RecordRow> {
    return api('/work/forms', {
      method: 'POST',
      body: JSON.stringify({ title, data }),
    });
  }
  /** Submit a form intake — the server validates required answers (400) and mints an audited
   *  form_submission with SERVER-SET provenance (submitted_by is never client-supplied). */
  submitWorkForm(
    id: string,
    answers: Record<string, unknown>
  ): Promise<RecordRow> {
    return api(`/work/forms/${encodeURIComponent(id)}/submit`, {
      method: 'POST',
      body: JSON.stringify({ answers }),
    });
  }
  listChecklistTemplatesPage(): Promise<RecordPage> {
    return this.page<RecordRow>('/work/checklists/templates');
  }
  createChecklistTemplate(
    title: string | null,
    data: Record<string, unknown>
  ): Promise<RecordRow> {
    return api('/work/checklists/templates', {
      method: 'POST',
      body: JSON.stringify({ title, data }),
    });
  }
  listChecklistInstancesPage(): Promise<RecordPage> {
    return this.page<RecordRow>('/work/checklists/instances');
  }
  createChecklistInstance(
    title: string | null,
    data: Record<string, unknown>
  ): Promise<RecordRow> {
    return api('/work/checklists/instances', {
      method: 'POST',
      body: JSON.stringify({ title, data }),
    });
  }
  /** The employee directory (redacted employee rows — the non-sensitive directory spine). */
  workDirectory(): Promise<RecordRow[]> {
    return api<RecordRow[]>('/work/directory');
  }
  /** The reports-to subtree below one employee (server-bounded depth/nodes; scoped; no oracle). */
  orgChart(rootEmployeeId: string): Promise<OrgChart> {
    return api<OrgChart>(
      `/work/org-chart/${encodeURIComponent(rootEmployeeId)}`
    );
  }
  /** The per-board / per-assignee work aggregates (computed, never stored; never blocks). */
  workDashboard(): Promise<WorkDashboardReport> {
    return api<WorkDashboardReport>('/work/dashboard');
  }

  directorContext(signal?: AbortSignal): Promise<DirectorContext> {
    return api('/director/context', { signal });
  }
  directorOverview(
    organizationId: string,
    companyId?: string,
    signal?: AbortSignal,
    cursor?: string
  ): Promise<DirectorOverview> {
    const query = new URLSearchParams({ organizationId, limit: '25' });
    if (companyId) query.set('companyId', companyId);
    if (cursor) query.set('cursor', cursor);
    return api(`/director/overview?${query}`, { signal });
  }
  directorCommitments(
    filters: {
      organizationId: string;
      companyId?: string;
      status?: DirectorCommitment['status'];
      cursor?: string;
    },
    signal?: AbortSignal
  ): Promise<DirectorPage<DirectorCommitment>> {
    const query = new URLSearchParams({
      organizationId: filters.organizationId,
      limit: '25',
    });
    if (filters.companyId) query.set('companyId', filters.companyId);
    if (filters.status) query.set('status', filters.status);
    if (filters.cursor) query.set('cursor', filters.cursor);
    return api(`/director/commitments?${query}`, { signal });
  }
  directorCommitment(
    id: string,
    signal?: AbortSignal
  ): Promise<DirectorDetail> {
    return api(`/director/commitments/${encodeURIComponent(id)}`, { signal });
  }
  directorHistory(
    id: string,
    cursor: string,
    signal?: AbortSignal
  ): Promise<DirectorPage<DirectorEvent>> {
    return api(
      `/director/commitments/${encodeURIComponent(id)}/history?${new URLSearchParams({ limit: '100', cursor })}`,
      { signal }
    );
  }
  directorPeople(
    companyId: string,
    query = '',
    signal?: AbortSignal,
    cursor?: string
  ): Promise<DirectorPage<DirectorPerson>> {
    const params = new URLSearchParams({ companyId, limit: '100' });
    if (query) params.set('search', query);
    if (cursor) params.set('cursor', cursor);
    return api(`/director/people?${params}`, { signal });
  }
  directorCreate(
    body: DirectorCreateInput
  ): Promise<{ commitment: DirectorCommitment }> {
    return api('/director/commitments', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  directorAction(
    id: string,
    body: DirectorActionInput
  ): Promise<{ commitment: DirectorCommitment; event: DirectorEvent }> {
    return api(`/director/commitments/${encodeURIComponent(id)}/actions`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  directorInbox(
    state: DirectorInboxState,
    signal?: AbortSignal,
    cursor?: string
  ): Promise<DirectorPage<DirectorInboxItem>> {
    const query = new URLSearchParams({ state, limit: '50' });
    if (cursor) query.set('cursor', cursor);
    return api(`/director/inbox?${query}`, { signal });
  }
  directorInboxAction(
    id: string,
    action: 'read' | 'acknowledge',
    body: { requestId: string; expectedVersion: number }
  ): Promise<DirectorInboxItem> {
    return api(`/director/inbox/${encodeURIComponent(id)}/${action}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  directorFeed(
    after: number,
    signal?: AbortSignal
  ): Promise<{
    items: DirectorInboxItem[];
    lastEventId: number;
    truncated: boolean;
  }> {
    return api(
      `/director/feed?${new URLSearchParams({ after: String(after), limit: '100' })}`,
      { signal }
    );
  }
  directorSetup(
    organizationId: string,
    signal?: AbortSignal
  ): Promise<DirectorSetup> {
    return api(
      `/director/admin/setup?${new URLSearchParams({ organizationId })}`,
      { signal }
    );
  }
  directorAdminDirectory(
    search: string,
    signal?: AbortSignal,
    cursor?: string
  ): Promise<
    DirectorPage<DirectorEligibleSubject> & {
      facilities: { items: DirectorFacility[]; truncated: boolean };
    }
  > {
    const query = new URLSearchParams({ search, limit: '100' });
    if (cursor) query.set('cursor', cursor);
    return api(`/director/admin/directory?${query}`, { signal });
  }
  directorAdminGrants(
    organizationId: string,
    signal?: AbortSignal,
    cursor?: string,
    companyId?: string
  ): Promise<DirectorPage<DirectorGrant>> {
    const query = new URLSearchParams({ organizationId, limit: '100' });
    if (companyId) query.set('companyId', companyId);
    if (cursor) query.set('cursor', cursor);
    return api(`/director/admin/grants?${query}`, { signal });
  }
  directorCreateOrganization(body: {
    requestId: string;
    name: string;
  }): Promise<DirectorOrganization> {
    return api('/director/admin/organizations', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  directorCreateCompany(body: {
    requestId: string;
    organizationId: string;
    key: string;
    name: string;
    facilityIds: string[];
  }): Promise<DirectorCompany> {
    return api('/director/admin/companies', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  directorUpdateCompany(
    id: string,
    body: {
      requestId: string;
      expectedVersion: number;
      name: string;
      facilityIds: string[];
    }
  ): Promise<DirectorCompany> {
    return api(`/director/admin/companies/${encodeURIComponent(id)}/update`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  directorCreateGrant(body: {
    requestId: string;
    organizationId: string;
    companyId: string | null;
    subjectId: string;
    role: DirectorRole;
  }): Promise<DirectorGrant> {
    return api('/director/admin/grants', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  directorRevokeGrant(
    id: string,
    body: { requestId: string; expectedVersion: number }
  ): Promise<DirectorGrant> {
    return api(`/director/admin/grants/${encodeURIComponent(id)}/revoke`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // --- QMS (4F) -------------------------------------------------------------------------------
  // Cookie routes /api/qms/* (qms.rs cookie_routes). The QMS entity grids (calibration_record,
  // controlled_document, ncr, internal_audit, management_review) reuse the generic
  // listRecords/createRecord/updateRecord/transition — no bespoke methods needed.
  /** The management-review inputs read-model (counts + non-sensitive enum labels only). */
  qmsDashboard(): Promise<QmsDashboardReport> {
    return api<QmsDashboardReport>('/qms/dashboard');
  }
  /** The employees × courses competence matrix (derived statuses; fail-soft names). */
  trainingMatrix(): Promise<TrainingMatrixReport> {
    return api<TrainingMatrixReport>('/qms/training-matrix');
  }

  // --- Refining (4D: lots / assays / bars) ----------------------------------------------------
  /** The lot→bar lineage read-model (cookie GET /api/refining/lots/:id/lineage). Derived,
   *  computed-never-stored, advisory — it reads the write-once `source_lot_ids` SoT server-side. */
  lotLineage(id: string): Promise<LotLineageReport> {
    return api<LotLineageReport>(
      `/refining/lots/${encodeURIComponent(id)}/lineage`
    );
  }
  /** The input→recovered→bar mass balance (cookie GET /api/refining/lots/:id/mass-balance).
   *  ADVISORY aggregates only — it never blocks a transition; render the numbers plainly. */
  lotMassBalance(id: string): Promise<LotMassBalanceReport> {
    return api<LotMassBalanceReport>(
      `/refining/lots/${encodeURIComponent(id)}/mass-balance`
    );
  }

  // --- Comms (4J: notifications / messages / send / subscriptions / test-event) ---------------
  /** The current actor's own notification inbox (server recipient-scoped on top of the partition). */
  listNotifications(): Promise<CommsRow[]> {
    return api<CommsRow[]>('/comms/notifications?limit=100');
  }
  /** The actor's own delivery preferences (`notification_subscription` rows, recipient-scoped). */
  listSubscriptions(): Promise<CommsRow[]> {
    return api<CommsRow[]>('/comms/subscriptions?limit=100');
  }
  /** The redacted external-comms feed (`email_message` rows; PII subject/addresses are stripped and
   *  the title masked server-side for a non-CustomerPii reader — render the redaction markers). */
  listMessages(): Promise<CommsRow[]> {
    return api<CommsRow[]>('/comms/messages?limit=200');
  }
  /** The raw stored MIME of one message (CustomerPii-gated server-side; a non-PII reader 403s —
   *  the screen renders that as an inline load error, never a crash). Text, not JSON. */
  async messageBody(id: string): Promise<string> {
    const res = await request(`/comms/messages/${encodeURIComponent(id)}/body`);
    return res.text();
  }
  /** Contact-bound outbound send (cookie POST /api/comms/send). Rejects with `ErpApiError` so the
   *  compose screen can distinguish the disabled-connector 409 (friendly notice) from a real error. */
  sendComms(body: CommsSendRequest): Promise<CommsRow> {
    return apiWithStatus<CommsRow>('/comms/send', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  /** Retry a failed outbound message. Same `ErpApiError` contract as `sendComms`. */
  retryMessage(id: string): Promise<CommsRow> {
    return apiWithStatus<CommsRow>(
      `/comms/messages/${encodeURIComponent(id)}/retry`,
      { method: 'POST' }
    );
  }
  /** Resolve + render a template without sending (an Erp READ — safe to call freely). */
  previewTemplate(body: CommsPreviewRequest): Promise<CommsPreviewResult> {
    return api<CommsPreviewResult>('/comms/templates/preview', {
      readOnly: true,
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  /** Drive the notifier seam with a hand-written event (SERVER-enforced admin-tier megaphone; the
   *  UI additionally gates the affordance on `useErpProvisioning().isAdmin`, fail-closed). */
  async sendTestEvent(body: CommsTestEventRequest): Promise<void> {
    await apiWithStatus<{ ok: boolean }>('/comms/test-event', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // --- Agreements + Inbound (4C: audit-trail timeline) -----------------------------------------
  /** The per-entity audit trail (cookie GET /api/entities/:id/audit) — newest first; every diff is
   *  already redacted server-side against the registry sensitivity SSOT. */
  auditTrail(id: string, limit = 100): Promise<AuditTrailEntry[]> {
    return api<AuditTrailEntry[]>(
      `/entities/${encodeURIComponent(id)}/audit?limit=${encodeURIComponent(String(limit))}`
    );
  }

  // --- IP2 Item Passport (cookie GET /api/entities/:id/passport) --------------------------------
  /** ONE item's whole life, assembled server-side: the redacted `subject` projection (carrying its
   *  own server-gated `allowed_transitions`), the structural spine, the merged event timeline, the
   *  per-source reports and the coverage/scope honesty metadata.
   *
   *  Every gate already ran in the core: `authorize_cap(Erp)` BEFORE any read, the facility
   *  partition on every base read, the restricted-type exclusion at the WHERE, and a fail-closed
   *  field-group drop that removes an ungranted source from `sources[]` ENTIRELY. A missing id, an
   *  out-of-partition id and a restricted-read type are ONE indistinguishable 404 — so the client
   *  must not try to tell them apart either. */
  itemPassport(id: string, opts?: PassportQuery): Promise<Passport> {
    const q = new URLSearchParams();
    if (opts?.since) q.set('since', opts.since);
    if (opts?.until) q.set('until', opts.until);
    if (opts?.sources?.length) q.set('sources', opts.sources.join(','));
    if (opts?.limit !== undefined) q.set('limit', String(opts.limit));
    const query = q.toString();
    return api<Passport>(
      `/entities/${encodeURIComponent(id)}/passport${query ? `?${query}` : ''}`
    );
  }

  /** Ziperase per-drive wipe-evidence pull (`POST /api/ziperase/pull/drive/:id`). Fetches ONLY this
   *  drive's wipe report from Ziperase and (for a wipe/data-security operator) attaches it; a receiver
   *  gets a dry-run probe. Server enforces all authority — this is an affordance, never a re-check.
   *  The counts tell the story: `matched_drive===0` = no report; `created>0` = attached;
   *  `already_recorded>0 && !dry_run` = already on file; `dry_run && matched_drive>0` = found, attaches
   *  at the wipe station. Non-ok throws `ErpApiError` (the server's own sentence) — the caller surfaces it. */
  getWipeEvidence(
    driveId: string,
    opts?: { certificate?: boolean }
  ): Promise<WipeEvidenceResult> {
    const q = opts?.certificate ? '?certificate=1' : '';
    return api<WipeEvidenceResult>(
      `/ziperase/pull/drive/${encodeURIComponent(driveId)}${q}`,
      { method: 'POST' }
    );
  }

  /** Ziperase per-asset full-MACHINE hardware scan pull (`POST /api/ziperase/hardware/asset/:id`).
   *  Fetches and attaches the full-machine `hardwares` scan (CPU/RAM/GPU/battery/component tests)
   *  for THIS asset — Ziperase captures it for EVERY erased device incl. PCs/Dells (verified live
   *  2026-08-22), so `matched_drive===0` only when the device was never scanned. With
   *  `certificate:true` it ALSO files the consolidated machine erasure-report PDF as evidence. Non-ok
   *  throws `ErpApiError`. */
  getAssetHardware(
    assetId: string,
    opts?: { certificate?: boolean }
  ): Promise<WipeEvidenceResult> {
    const q = opts?.certificate ? '?certificate=1' : '';
    return api<WipeEvidenceResult>(
      `/ziperase/hardware/asset/${encodeURIComponent(assetId)}${q}`,
      { method: 'POST' }
    );
  }

  // --- Sales (4G: leads / quotes / pipeline / campaigns / activities / outreach) ---------------
  // Cookie routes /api/sales/* (sales.rs cookie_routes). Creates go through the typed /sales
  // surface (each route mints its own kind — no type smuggling); updates + lifecycle advances
  // reuse the generic updateRecord/transition (the SAME update_entity/transition_entity SSOT the
  // sales routes delegate to — one write chokepoint, no second path).
  /** The leads funnel list (Erp-gated + facility-scoped + redacted server-side). */
  listLeadsPage(): Promise<RecordPage> {
    return this.page<RecordRow>('/sales/leads');
  }
  createLead(
    title: string | null,
    data: Record<string, unknown>
  ): Promise<RecordRow> {
    return api('/sales/leads', {
      method: 'POST',
      body: JSON.stringify({ title, data }),
    });
  }
  /** Convert a WON lead into a customer account (server-idempotent; keys off the STORED status).
   *  Rejects with `ErpApiError` so the screen can surface the server guard message inline. */
  convertLead(id: string): Promise<LeadConvertReceipt> {
    return apiWithStatus<LeadConvertReceipt>(
      `/sales/leads/${encodeURIComponent(id)}/convert`,
      { method: 'POST' }
    );
  }
  /** The quotes list (Financials/BidMargin scalars arrive redacted for ungranted readers). */
  listQuotesPage(): Promise<RecordPage> {
    return this.page<RecordRow>('/sales/quotes');
  }
  createQuote(
    title: string | null,
    data: Record<string, unknown>
  ): Promise<RecordRow> {
    return api('/sales/quotes', {
      method: 'POST',
      body: JSON.stringify({ title, data }),
    });
  }
  /** Convert an ACCEPTED quote into a draft service agreement (server-idempotent — a replay
   *  returns the same agreement with `created: false`). Rejects with `ErpApiError`. */
  convertQuote(id: string): Promise<QuoteConvertReceipt> {
    return apiWithStatus<QuoteConvertReceipt>(
      `/sales/quotes/${encodeURIComponent(id)}/convert`,
      { method: 'POST' }
    );
  }
  /** Email a quote's customer contact through the comms seam (contact-bound — the recipient
   *  resolves server-side; a disabled connector is a clean 409 the caller maps to a notice). */
  sendQuote(id: string, req: OutreachRequest): Promise<OutreachReceipt> {
    return apiWithStatus<OutreachReceipt>(
      `/sales/quotes/${encodeURIComponent(id)}/send`,
      { method: 'POST', body: JSON.stringify(req) }
    );
  }
  /** Log a `sales_activity` CRM touch (call/meeting/note) through the create SSOT. */
  createSalesActivity(
    title: string | null,
    data: Record<string, unknown>
  ): Promise<RecordRow> {
    return api('/sales/activities', {
      method: 'POST',
      body: JSON.stringify({ title, data }),
    });
  }
  /** Contact-bound outreach send + the server-logged activity (POST /api/sales/outreach). */
  sendOutreach(req: OutreachRequest): Promise<OutreachReceipt> {
    return apiWithStatus<OutreachReceipt>('/sales/outreach', {
      method: 'POST',
      body: JSON.stringify(req),
    });
  }
  /** The sales pipeline read-model (funnel counts; `withValue` adds the Financials-gated value
   *  section — the server 403s a non-Financials actor, NEVER a zeroed section, so callers retry
   *  without the value section and render the localized restricted note). */
  salesPipeline(withValue: boolean): Promise<PipelineReport> {
    return apiWithStatus<PipelineReport>(
      `/sales/pipeline${withValue ? '?value=true' : ''}`
    );
  }
  /** The campaign-ROI read-model — Financials-gated WHOLESALE (a non-Financials actor 403s;
   *  render the localized restricted state, never a zeroed report). */
  campaignRoi(id: string): Promise<CampaignRoiReport> {
    return apiWithStatus<CampaignRoiReport>(
      `/sales/campaigns/${encodeURIComponent(id)}/roi`
    );
  }

  // --- Listings back-office (4E: listings / sellable stock / order payment) --------------------
  // Cookie routes /api/listings* + /api/orders/:id/payment-status (store.rs cookie_routes).
  /** The internal listings list (cost/margin arrive redacted for ungranted readers). */
  listListingsPage(): Promise<RecordPage> {
    return this.page<RecordRow>('/listings');
  }
  createListing(
    title: string | null,
    data: Record<string, unknown>
  ): Promise<RecordRow> {
    return api('/listings', {
      method: 'POST',
      body: JSON.stringify({ title, data }),
    });
  }
  /** Edit a listing. Rejects with `ErpApiError` so a SOLD listing's server-side field freeze
   *  (409) renders inline in the still-open form — never a silent generic failure. */
  updateListing(
    id: string,
    title: string | null,
    data: Record<string, unknown>
  ): Promise<RecordRow> {
    return apiWithStatus<RecordRow>(`/listings/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ title, data }),
    });
  }
  /** Advance a listing via its named back-office action (publish/unpublish/delist) — thin
   *  delegates over the SAME transition_entity SSOT; the affordance still renders strictly from
   *  the server's `allowed_transitions` (enshrined 4B.6). */
  publishListing(id: string): Promise<RecordRow> {
    return apiWithStatus<RecordRow>(
      `/listings/${encodeURIComponent(id)}/publish`,
      { method: 'POST' }
    );
  }
  unpublishListing(id: string): Promise<RecordRow> {
    return apiWithStatus<RecordRow>(
      `/listings/${encodeURIComponent(id)}/unpublish`,
      { method: 'POST' }
    );
  }
  delistListing(id: string): Promise<RecordRow> {
    return apiWithStatus<RecordRow>(
      `/listings/${encodeURIComponent(id)}/delist`,
      { method: 'POST' }
    );
  }
  /** Sellable in-stock inventory not yet actively listed — the create-form picker source. */
  inventoryAvailable(): Promise<AvailableStock[]> {
    return api<AvailableStock[]>('/listings/inventory-available');
  }
  /** The finance payment attestation on an outbound order (Financials-gated server-side — a
   *  non-Financials actor's 403 renders inline; rejects with `ErpApiError`). */
  setOrderPaymentStatus(id: string, paymentStatus: string): Promise<RecordRow> {
    return apiWithStatus<RecordRow>(
      `/orders/${encodeURIComponent(id)}/payment-status`,
      {
        method: 'POST',
        body: JSON.stringify({ payment_status: paymentStatus }),
      }
    );
  }

  // --- Diagram canvas mint (org chart → edgeless canvas) ---------------------------------------
  /** Mint a subject entity's relationship neighbourhood as an EDITABLE diagram onto a page doc's
   *  edgeless canvas (cookie POST /api/diagram/generate). The audited sync push is attributed to
   *  the ACTING USER: the browser first mints its own sync token (GET /api/auth/sync-token, the
   *  same token the workspace sync socket uses) and carries it in the Authorization header —
   *  never a service account (non-negotiable #3). The session cookie still authorizes the
   *  handler itself (the one `generate` + `authorize_cap(Erp)` + actor-scoped graph read). */
  async mintDiagram(req: {
    workspaceId: string;
    docId: string;
    entityId: string;
    depth?: number;
    rel?: string;
  }): Promise<DiagramMintResult> {
    const tokenRes = await fetch('/api/auth/sync-token', {
      credentials: 'include',
      headers: { accept: 'application/json' },
    });
    if (!tokenRes.ok) {
      throw await toErpApiError(tokenRes, 'GET', '/auth/sync-token');
    }
    const { token } = (await tokenRes.json()) as { token?: unknown };
    if (typeof token !== 'string') {
      throw new Error('sync token response missing token');
    }
    const res = await fetch('/api/diagram/generate', {
      method: 'POST',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
        // Core-bypass fetch (bearer, not the shared request core) — carry the pinned site view
        // explicitly so the minted diagram's graph walk matches the screens (site-scope audit).
        ...siteScopeHeaders(),
      },
      body: JSON.stringify({
        workspace_id: req.workspaceId,
        doc_id: req.docId,
        entity_id: req.entityId,
        ...(req.depth !== undefined ? { depth: req.depth } : {}),
        ...(req.rel ? { rel: req.rel } : {}),
      }),
    });
    if (!res.ok) {
      throw await toErpApiError(res, 'POST', '/diagram/generate');
    }
    return (await res.json()) as DiagramMintResult;
  }

  // --- Reports (ROMS+Monday P0.1) --------------------------------------------------------------
  // Cookie twins of the bearer /reports + /reports/run (one Erp-gated core each; the catalog +
  // every run is server-authoritative — the screen never hardcodes a template list or re-derives
  // a column's sensitivity: ungranted columns are already DROPPED fail-closed by the runner).
  /** The report-template catalog (key + titles + typed param schema) — the picker's ONLY source. */
  listReportTemplates(): Promise<ReportCatalogEntry[]> {
    return api<{ reports: ReportCatalogEntry[] }>('/reports').then(
      r => r.reports
    );
  }
  /** Run one report. `params` must match the template's declared schema (the server 400s a
   *  mismatch — surfaced inline via `ErpApiError`; a template may additionally 403 on its own
   *  field-group gate, carried as `ErpApiError.status` so a screen renders a friendly not-granted
   *  notice instead of a crash). Rows arrive column-gated + partition-scoped. */
  runReport(
    key: string,
    params?: Record<string, unknown> | null
  ): Promise<ReportRunResult> {
    return apiWithStatus<ReportRunResult>('/reports/run', {
      readOnly: true,
      method: 'POST',
      body: JSON.stringify({ key, params: params ?? null }),
    });
  }
  /** Delete an entity via the audited cookie twin (used by the Reports screen's saved presets —
   *  same `delete_core` guard sequence as the bearer route). 204 → no body. */
  deleteRecord(id: string): Promise<void> {
    return apiNoContent(`/entities/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  // --- Bids intake (P3.1, track D: the Dell bid intake workflow) -------------------------------
  /** The bid queue (`bid_lot` rows via the generic records core; the CustomerPii title arrives
   *  masked for ungranted readers; each row carries the server-DERIVED `fmv_delta_pct` and the
   *  server `email_bid_allowed` affordance — the UI never re-derives either). */
  listBidLotsPage(): Promise<RecordPage<BidLotRow>> {
    return this.listRecordsPage('bid_lot') as Promise<RecordPage<BidLotRow>>;
  }
  /** Email a bid file to the owning customer's contact-of-record (cookie POST
   *  /api/bids/:id/email-bid). The recipient AND the artifact are tenant-bound server-side;
   *  rejects with `ErpApiError` so the screen maps the disabled-connector 409 to the friendly
   *  localized notice (the comms compose convention). */
  emailBid(id: string, req: EmailBidRequest): Promise<RecordRow> {
    return apiWithStatus<RecordRow>(
      `/bids/${encodeURIComponent(id)}/email-bid`,
      { method: 'POST', body: JSON.stringify(req) }
    );
  }
  /** Price a Dell workbook through the bid engine (cookie POST /api/bids/optimize — STATELESS:
   *  nothing persisted; the Bids screen attaches the summary totals to the lot via the generic
   *  updateRecord). Raw-bytes body (the document-upload fetch idiom — the JSON request core
   *  would mislabel the workbook). */
  async optimizeBidWorkbook(file: File): Promise<BidOptimizeResult> {
    const path = '/bids/optimize';
    const res = await fetch(`/api${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        ...siteScopeHeaders(),
        'content-type':
          file.type ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      body: file,
    });
    if (!res.ok) {
      throw await toErpApiError(res, 'POST', path);
    }
    return (await res.json()) as BidOptimizeResult;
  }

  // --- Portal quote requests — the INTERNAL ops queue (P3.2, track D) --------------------------
  /** The quote-request queue (`quote_request` rows via the generic records core; CustomerPii
   *  requester/contact scalars arrive redacted for ungranted readers; advance affordances come
   *  from each row's server `allowed_transitions` — never re-derived). */
  listQuoteRequestsPage(): Promise<RecordPage> {
    return this.listRecordsPage('quote_request');
  }
  /** Convert a live quote request into a draft quote feeding the existing quote pipeline
   *  (cookie POST /api/quote-requests/:id/convert — server-idempotent; keys off the STORED
   *  status; the write-once pointer is server-set). Rejects with `ErpApiError` so the screen
   *  surfaces the server guard message inline. */
  convertQuoteRequest(id: string): Promise<QuoteRequestConvertReceipt> {
    return apiWithStatus<QuoteRequestConvertReceipt>(
      `/quote-requests/${encodeURIComponent(id)}/convert`,
      { method: 'POST' }
    );
  }

  // --- Track E (ROMS+Monday P4.7 staff position tracking) --------------------------------------
  /** MY OWN position pings (GET /api/me/position — the server binds employee_ref = self and the
   *  one serializer's self-scope keeps my own coordinates visible to me). */
  listMyPositions(): Promise<RecordRow[]> {
    return api<RecordRow[]>('/me/position?limit=500');
  }
  /** Report my current position (consent-gated SERVER-side: 403 until an admin enables tracking
   *  on my employee record — render that as the "not enabled" notice, never a crash). */
  sendMyPosition(
    lat: number,
    lng: number,
    accuracy?: number
  ): Promise<MyPositionReceipt> {
    return apiWithStatus<MyPositionReceipt>('/me/position', {
      method: 'POST',
      body: JSON.stringify({
        lat,
        lng,
        ...(accuracy !== undefined ? { accuracy } : {}),
      }),
    });
  }

  assistantThreads(
    workspaceId: string,
    offset = 0,
    search = '',
    signal?: AbortSignal
  ): Promise<AssistantPage> {
    const query = new URLSearchParams({
      workspaceId,
      offset: String(offset),
      search,
    });
    return api(`/assistant/threads?${query}`, { signal });
  }
  createAssistantThread(
    workspaceId: string,
    signal?: AbortSignal
  ): Promise<{ id: string }> {
    return api('/assistant/threads', {
      method: 'POST',
      body: JSON.stringify({ workspaceId }),
      signal,
    });
  }
  assistantHistory(
    id: string,
    before?: string,
    signal?: AbortSignal
  ): Promise<AssistantHistory> {
    const query = before ? `?before=${encodeURIComponent(before)}` : '';
    return api(`/assistant/threads/${encodeURIComponent(id)}${query}`, {
      signal,
    });
  }
  async assistantSend(
    id: string,
    question: string,
    mode: AssistantMode,
    requestId: string,
    opts: {
      signal: AbortSignal;
      onSaved: (message: AssistantMessage) => void;
      onStatus: (status: AskStatus) => void;
      onDelta: (text: string) => void;
    }
  ): Promise<AssistantMessage> {
    const res = await request(
      `/assistant/threads/${encodeURIComponent(id)}/messages`,
      {
        method: 'POST',
        body: JSON.stringify({ question, mode, requestId }),
        signal: opts.signal,
      }
    );
    if (!res.body) throw new AskError('ask_stream', 'empty response body');
    for await (const ev of readSseEvents(res.body)) {
      if (ev.event === 'saved')
        opts.onSaved(JSON.parse(ev.data) as AssistantMessage);
      if (ev.event === 'status') {
        const status = parseAskStatus(ev.data);
        if (status) opts.onStatus(status);
      }
      if (ev.event === 'delta') {
        const value: unknown = JSON.parse(ev.data);
        if (
          value &&
          typeof value === 'object' &&
          'text' in value &&
          typeof value.text === 'string'
        )
          opts.onDelta(value.text);
      }
      if (ev.event === 'answer') return JSON.parse(ev.data) as AssistantMessage;
      if (ev.event === 'error')
        throw new AskError('ask_stream', 'response interrupted');
    }
    throw new AskError('ask_stream', 'stream ended without a saved answer');
  }

  // --- Ask PIPE (read-only natural-language query; spec docs/superpowers/specs/2026-08-16-ask-pipe-design.md)
  /** One ask, streamed: cookie `POST /api/ask?stream=true` through the ONE `request()` core (site
   *  scope rides — narrow-only is right for a READ), SSE `status` events reach `onStatus` per hop,
   *  and the terminal `answer` envelope resolves. A pre-stream refusal (400/403/503) rejects with
   *  `ErpApiError` (its `code` carries `ask_offbox` / `ask_llm_down`); an in-stream `error` event
   *  rejects with `AskError`. The server is the authority — every tool result behind the answer is
   *  the same scoped + redacted projection the screens get, and the citations are server-collected. */
  async ask(
    req: AskRequest,
    opts: { onStatus?: (status: AskStatus) => void; signal?: AbortSignal } = {}
  ): Promise<AskAnswer> {
    const body: AskRequest = {
      question: req.question,
      ...(req.history && req.history.length > 0
        ? { history: req.history }
        : {}),
    };
    const res = await request('/ask?stream=true', {
      readOnly: true,
      method: 'POST',
      body: JSON.stringify(body),
      signal: opts.signal,
    });
    if (!res.body) {
      throw new AskError('ask_stream', 'empty response body');
    }
    for await (const ev of readSseEvents(res.body)) {
      if (ev.event === 'status') {
        const status = parseAskStatus(ev.data);
        if (status) opts.onStatus?.(status);
      } else if (ev.event === 'answer') {
        return JSON.parse(ev.data) as AskAnswer;
      } else if (ev.event === 'error') {
        const { code, message } = JSON.parse(ev.data) as {
          code?: unknown;
          message?: unknown;
        };
        throw new AskError(
          typeof code === 'string' ? code : 'ask_stream',
          typeof message === 'string' ? message : ''
        );
      }
    }
    throw new AskError('ask_stream', 'stream ended without an answer');
  }

  // --- Floor stations (docs/floor-stations-contract.md) -----------------------------------------
  // The five station screens act through THESE methods and no other path. Each is the cookie twin
  // of an already-built, already-guarded bearer route (`/api/*`, which Caddy proxies un-stripped),
  // hitting the SAME server core — so the authority stays entirely server-side and nothing here
  // re-derives a role, a stage or an FSM edge. A refusal rejects with `ErpApiError` carrying the
  // server's own guard message, which is the most useful line on a floor screen: surface it.

  /** Pickup dispatch: assign a licensed driver (chosen as an `employee` id) to a scheduled
   *  collection. Cookie twin of the bearer `POST /ops/pickups/:id/assign` — the SAME
   *  `entity::assign_pickup_core`: Erp write gate → `may_assign_driver` → scoped employee load →
   *  the 4F.5 driver-qualification gate → `keycloak_subject` resolve → the audited `update_entity`
   *  `assigned_to` branch. Rejects 403 (not a dispatcher), 404 (no such VISIBLE employee or
   *  pickup — never an existence oracle), 409 (`[QMS-COMPETENCE]` licence/training not current) or
   *  400 (that employee has no linked login). Resolves to the redacted pickup row. */
  assignPickup(pickupId: string, employeeId: string): Promise<RecordRow> {
    return api<RecordRow>(
      `/ops/pickups/${encodeURIComponent(pickupId)}/assign`,
      {
        method: 'POST',
        body: JSON.stringify({ employee_id: employeeId }),
      }
    );
  }
  /** Outbound: reserve the load's stock in ONE transaction (order → `allocated`, every line item →
   *  `reserved`). No request body — the server derives everything from the order. All-or-nothing:
   *  a line the actor cannot see 404s and NOTHING moves. Rejects with the guard's own message
   *  (e.g. the expired-vendor block, the allocation precondition). Resolves to the redacted order. */
  allocateOutbound(id: string): Promise<RecordRow> {
    return api<RecordRow>(`/outbound/${encodeURIComponent(id)}/allocate`, {
      method: 'POST',
    });
  }
  /** Outbound: ship the load in ONE transaction (order `packed`→`shipped`, each reserved line →
   *  `shipped`). No body; same all-or-nothing rule and the same honest guard messages as allocate.
   *  It deliberately does NOT mint shipment evidence — that is a separate, staff-created record. */
  shipOutbound(id: string): Promise<RecordRow> {
    return api<RecordRow>(`/outbound/${encodeURIComponent(id)}/ship`, {
      method: 'POST',
    });
  }
  /** Outbound: mint a DRAFT Bill of Lading prefilled from the shipment (server-side derivation off
   *  already-scoped reads, minted through the ONE audited create core). Print it with
   *  `bolPdfUrl(bol.id)`. Resolves to the redacted `bill_of_lading` row. */
  mintOutboundBol(outboundId: string): Promise<RecordRow> {
    return api<RecordRow>(
      `/ops/outbound/${encodeURIComponent(outboundId)}/bol`,
      {
        method: 'POST',
      }
    );
  }
  /** Invoicing: the finance review queue — the orders the server considers ready to bill. Erp-gated
   *  + facility-scoped + redacted server-side; advisory (it blocks nothing). */
  financeReviewQueue(limit = STATION_QUEUE_LIMIT): Promise<RecordPage> {
    return this.pageOf(`/finance/orders/review?limit=${limit}`);
  }
  /** Invoicing: mint an invoice DRAFT for a completed order through the audited create core (it
   *  requires an approved service agreement and refuses a second live invoice with a 409). The
   *  invoice inherits the order's site — which is what makes per-site invoice numbering work — and
   *  its customer. Resolves to the redacted `invoice` row. */
  draftInvoice(orderId: string): Promise<RecordRow> {
    return api<RecordRow>(
      `/finance/orders/${encodeURIComponent(orderId)}/invoice-draft`,
      { method: 'POST' }
    );
  }
  /** Invoicing: the line-by-line money breakdown, recomputed server-side from the invoice's FROZEN
   *  basis (so an issued invoice always previews identically). **Financials-gated**: a reader
   *  without that grant gets a 403 — render that as "you cannot see the money detail", NEVER as an
   *  empty or zeroed invoice. */
  invoicePreview(invoiceId: string): Promise<InvoiceDraft> {
    return api<InvoiceDraft>(
      `/finance/invoices/${encodeURIComponent(invoiceId)}/preview`
    );
  }
  /** Invoicing: issue a drafted invoice — ONE transaction that takes the next per-site invoice
   *  number, advances it to `issued` and freezes its money. Only a `pending_review` invoice can be
   *  issued (a re-issue is refused, not silently repeated). Resolves to the redacted invoice. */
  issueInvoice(invoiceId: string): Promise<RecordRow> {
    return api<RecordRow>(
      `/finance/invoices/${encodeURIComponent(invoiceId)}/issue`,
      { method: 'POST' }
    );
  }
}

// ================================================================================================
// Frontend wave FB (refining / comms / agreements+inbound) — DTOs + a status-preserving fetch
// helper. Appended as ONE region (the wave contract: shared-file edits stay append-style).
// ================================================================================================

/** A fetch failure that preserves the HTTP status + the server's `{"error": …}` detail. The comms
 *  compose screen branches on the disabled-connector 409; everything else renders `message` inline.
 *  A subclass of Error, so every existing `e instanceof Error` catch-site keeps working. */
export class ErpApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail: string,
    /** The server's machine-readable `code` when the error body carries one (ask-PIPE's
     *  `ask_offbox` / `ask_llm_down` 503s); undefined for every other error shape. */
    readonly code?: string
  ) {
    super(message);
    this.name = 'ErpApiError';
  }
}

/** True when a rejection is an AUTH failure (401/403) — a session-expiry/role-revocation signal
 *  the SWR screens must SURFACE even over cached rows (a network blip they may suppress). */
export function isAuthError(e: unknown): boolean {
  return e instanceof ErpApiError && (e.status === 401 || e.status === 403);
}

/** Build an `ErpApiError` from a non-ok Response (same detail extraction as `api()`). */
async function toErpApiError(
  res: Response,
  method: string,
  path: string
): Promise<ErpApiError> {
  let detail = '';
  let code: string | undefined;
  try {
    const j = await res.clone().json();
    detail = (j && (j.error ?? j.message)) || '';
    if (j && typeof j.code === 'string') code = j.code;
  } catch {
    detail = await res.text().catch(() => '');
  }
  return new ErpApiError(
    `pipe api ${method} ${path}: ${res.status}${detail ? ` — ${detail}` : ''}`,
    res.status,
    detail,
    code
  );
}

/** Historical alias from the wave's append-only era — `api()` itself now preserves the HTTP
 *  status on failure (every helper throws `ErpApiError` through the one `request()` core). */
const apiWithStatus = api;

// --- Refining DTOs (field-for-field with `refining.rs` LotLineageReport / MassBalanceReport) ----
export interface LineageBar {
  bar_id: string;
  serial_no: string | null;
  metal: string | null;
  mass_g: number | null;
  state: string | null;
  lbma_compliant: boolean | null;
}
export interface LotLineageReport {
  lot_id: string;
  bars: LineageBar[];
}
export interface LotMassBalanceReport {
  lot_id: string;
  /** The sealed lot weight normalized to grams; null when the lot is unweighed. */
  input_mass_g: number | null;
  recovered_mass_g: number;
  bar_mass_g: number;
  variance_pct: number;
  input_recovered_variance_pct: number | null;
  tolerance_pct: number;
  /** ADVISORY recovered↔bar reconcile verdict — display only, never gates anything client-side. */
  reconciled: boolean;
}

// --- Comms DTOs --------------------------------------------------------------------------------
/** An entity row from the comms feeds — a `RecordRow` plus the emission timestamps the single
 *  redaction serializer always carries (used by the feed's "when" column). */
export interface CommsRow extends RecordRow {
  created_at?: string;
  updated_at?: string;
}
/** POST /api/comms/send — the recipient resolves SERVER-side from a visible contact (never a raw
 *  address in the body). */
export interface CommsSendRequest {
  template: string;
  contact_id: string;
  vars?: Record<string, string>;
  inbound_order_id?: string;
}
export interface CommsPreviewRequest {
  name: string;
  locale: string;
  vars?: Record<string, string>;
}
export interface CommsPreviewResult {
  template_id: string;
  locale_used: string;
  subject: string;
  body: string;
}
export interface CommsTestEventRequest {
  kind: string;
  summary: string;
  entity_id?: string;
  facility_id?: string;
}

// --- Agreements + Inbound DTOs ------------------------------------------------------------------
/** One audit-trail row (field-for-field with the Rust `AuditTrailEntry`); `diff` is server-redacted. */
export interface AuditTrailEntry {
  at: string;
  /** IP3.2: the acting person's server-resolved employee display name; `null` when no employee row
   *  visible to the reader maps — never a raw actor UUID. */
  actor: string | null;
  action: string;
  diff: unknown;
}

// ================================================================================================
// Frontend wave F5 (sales / listings / org-chart canvas) — DTOs. Appended as ONE region (the wave
// contract: shared-file edits stay append-style).
// ================================================================================================

// --- Sales DTOs (field-for-field with `sales.rs` receipts + read-models) -------------------------
/** POST /api/sales/leads/:id/convert — `converted: false` = a clean idempotent replay. */
export interface LeadConvertReceipt {
  account_id: string;
  converted: boolean;
}
/** POST /api/sales/quotes/:id/convert — `created: false` = a clean idempotent replay. */
export interface QuoteConvertReceipt {
  agreement_id: string;
  created: boolean;
}
/** POST /api/sales/outreach | /api/sales/quotes/:id/send — the recipient resolves SERVER-side
 *  from a visible contact (never a raw address in the body — the 4J.2 send_core guarantee). */
export interface OutreachRequest {
  template: string;
  contact_id: string;
  vars?: Record<string, string>;
  lead_id?: string;
  quote_id?: string;
  account_ref?: string;
  campaign_id?: string;
  inbound_order_id?: string;
  subject?: string;
}
/** `activity_id` is null iff the best-effort post-egress CRM log failed (the mail still sent). */
export interface OutreachReceipt {
  email_message_id: string;
  activity_id: string | null;
}
/** A count of entities at one funnel status (non-sensitive enum label + count only). */
export interface StatusCount {
  status: string;
  count: number;
}
/** The Financials-gated pipeline value section — present only for a Financials holder who
 *  requested it (the server 403s otherwise; NO margin/cost aggregate is ever emitted). */
export interface PipelineValue {
  open_lead_value: number;
  won_lead_value: number;
  open_quote_value: number;
  weighted_lead_value: number;
}
/** GET /api/sales/pipeline — funnel counts (Erp) + the optional Financials value section. */
export interface PipelineReport {
  lead_funnel: StatusCount[];
  quote_funnel: StatusCount[];
  value: PipelineValue | null;
}
/** GET /api/sales/campaigns/:id/roi — Financials-gated wholesale; `roi` is null when the
 *  campaign has no budget (an un-budgeted campaign has no defined ROI — never a fake zero). */
export interface CampaignRoiReport {
  campaign_id: string;
  budget: number;
  leads_total: number;
  leads_won: number;
  won_value: number;
  roi: number | null;
}

// --- Listings DTOs (field-for-field with `store.rs`) ---------------------------------------------
/** One sellable-stock row (GET /api/listings/inventory-available) — ids + non-sensitive stock
 *  fields only (`qty` is emitted as jsonb text by the read-model). */
export interface AvailableStock {
  id: string;
  facility_id: string | null;
  sku: string | null;
  source_type: string | null;
  location: string | null;
  qty: string | null;
  uom: string | null;
}

// --- Diagram canvas mint DTO (field-for-field with `diagram.rs` DiagramResult) -------------------
export interface DiagramMintResult {
  workspace_id: string;
  doc_id: string;
  entity_id: string;
  nodes: number;
  edges: number;
  minted: number;
}

// ================================================================================================
// Reports (ROMS+Monday P0.1) — DTOs, field-for-field with the Rust `reports/mod.rs` catalog + run
// emissions. Appended as ONE region (the wave contract: shared-file edits stay append-style).
// ================================================================================================

/** The server's `ParamType` wire tokens (serde snake_case) — the param form renders from these. */
export type ReportParamType =
  | 'date_range'
  | 'account_ref'
  | 'order_ref'
  | 'facility_ref'
  | 'serial'
  | 'category'
  | 'text'
  | 'flag'
  | 'number';
/** One declared parameter of a report template (GET /api/reports). */
export interface ReportParamSpec {
  key: string;
  type: ReportParamType;
  required: boolean;
}
/** One catalog entry — the picker renders STRICTLY from this server list (never hardcoded). */
export interface ReportCatalogEntry {
  key: string;
  title: string;
  title_i18n: string;
  params: ReportParamSpec[];
}
/** One output column of a finished run. Sensitive columns the reader lacks NEVER appear — the
 *  runner drops them fail-closed server-side, so absence here is authoritative. */
export interface ReportColumn {
  key: string;
  title: string;
  title_i18n: string;
}
/** Track E compatibility alias — ONE column DTO (the reports SSOT), two historical names. */
export type ReportRunColumn = ReportColumn;
/** POST /api/reports/run — cells are plain JSON scalars (already redaction-safe server-side);
 *  `truncated: true` means the run hit the server row ceiling (render the banner — never
 *  silently partial). */
export interface ReportRunResult {
  key: string;
  title: string;
  title_i18n: string;
  columns: ReportColumn[];
  rows: unknown[][];
  footer_totals: Record<string, number>;
  truncated: boolean;
}

// ================================================================================================
// P3.1 (track D) — Bids intake DTOs (field-for-field with the Core API's serializer extras +
// EmailBidBody + the /bids/optimize summary). Appended as ONE region (the wave contract).
// ================================================================================================

/** A `bid_lot` row plus the two P3.1 serializer extras (internal projection only). */
export interface BidLotRow extends RecordRow {
  /** Server-DERIVED (computed-never-stored): (bid + 3rd-party − FMV) / FMV × 100. Absent when
   *  the lot has no positive FMV or no bid figure — never a fabricated 0%. */
  fmv_delta_pct?: number;
  /** Server affordance (4B.6): render the Email-bid button STRICTLY from this flag — true exactly
   *  where the write gate would pass (Erp write + an account on record; agents always false). */
  email_bid_allowed?: boolean;
}

/** POST /api/bids/:id/email-bid body (the server resolves recipient + attachment tenant-bound). */
export interface EmailBidRequest {
  contact_id: string;
  /** Template name; the server defaults to `bid_delivery`. */
  template?: string;
  /** Which bid-file slot to attach; the server defaults to the completed bid when set. */
  file_field?: 'dell_bid_file' | 'customer_bid_file' | 'bid_complete_file';
}

/** The non-sensitive half of the /bids/optimize summary the Bids screen attaches to a lot. */
export interface BidOptimizeSummary {
  lot_fmv_total: number;
  ext_value_total: number;
  rows_filled: number;
  lot_viable: boolean;
}

/** POST /api/bids/optimize response (stateless pricing — `bid_lot_id` set only on persist runs). */
export interface BidOptimizeResult {
  summary: BidOptimizeSummary;
  bid_lot_id: string | null;
}

// ================================================================================================
// P3.2 (track D) — Portal quote-request DTOs (the internal queue side). Appended as ONE region.
// ================================================================================================

/** POST /api/quote-requests/:id/convert response. */
export interface QuoteRequestConvertReceipt {
  quote_id: string;
  converted: boolean;
}

/** The self-position ingest receipt (`POST /api/me/position`). */
export interface MyPositionReceipt {
  id: string;
  ts: string;
}

// ================================================================================================
// Ask PIPE (2026-08-16) — the wire contract of `POST /api/ask` (spec
// docs/superpowers/specs/2026-08-16-ask-pipe-design.md §Wire contract), field-for-field. Appended
// as ONE region. Test fixtures mirror these shapes byte-for-byte (the IP4 lesson).
// ================================================================================================

/** One prior Q/A turn the client replays (≤6, newest last; server caps q ≤2000 / a ≤4000). */
export interface AskHistoryTurn {
  q: string;
  a: string;
}

/** The request body. `history` is optional (omitted when empty). */
export interface AskRequest {
  question: string;
  history?: AskHistoryTurn[];
}

/** SSE `status` event — one per tool hop. */
export interface AskStatus {
  hop: number;
  tool: string;
}

/** A SERVER-collected citation (never model-claimed): an id the redacted projections actually
 *  carried. `title` is null with `title_redacted: true` when the reader may not see it. */
export interface AskCitation {
  id: string;
  type: string;
  title: string | null;
  title_redacted: boolean;
}

/** The AnswerEnvelope — plain text (rendered pre-wrap, never as markdown), ≤10 citations,
 *  `truncated` when any tool result was budget-trimmed, `no_answer` for an honest miss (a 200).
 *  `unverified` (v1.2) lists figures/identifiers in `text` the server could not verify against
 *  any tool result the model saw — an honest hedge, never a redaction signal. */
export interface AskAnswer {
  text: string;
  citations: AskCitation[];
  tools_used: string[];
  hops: number;
  truncated: boolean;
  outcome: 'answered' | 'no_answer';
  unverified: string[];
}

/** An in-stream failure (`error` SSE event, or a stream that closed without a terminal event).
 *  `code` is the server's (`ask_llm_down`, …) or the client-side `ask_stream`. */
export class AskError extends Error {
  constructor(
    readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'AskError';
  }
}

/** Tolerant status-event decode: a malformed hop line is DROPPED (it is only UX), never fatal. */
function parseAskStatus(data: string): AskStatus | null {
  try {
    const j = JSON.parse(data) as { hop?: unknown; tool?: unknown };
    if (typeof j.hop === 'number' && typeof j.tool === 'string') {
      return { hop: j.hop, tool: j.tool };
    }
  } catch {
    // fall through — dropped
  }
  return null;
}

// ================================================================================================
// Floor stations (docs/floor-stations-contract.md) — DTOs, field-for-field with the Rust
// `finance.rs` InvoiceDraft / DraftLine. Appended as ONE region (the wave contract: shared-file
// edits stay append-style).
// ================================================================================================

/** One computed invoice line. The server RE-computes these from the invoice's frozen basis on every
 *  preview and NEVER stores them, so this is a read-model row, not a record. `direction` is the
 *  server's own token (a charge to the customer vs. a credit back to them). */
export interface DraftLine {
  kind: string;
  code: string;
  description: string | null;
  qty: number;
  uom: string | null;
  rate: number;
  amount: number;
  direction: string;
}

/** The recomputed invoice breakdown behind `invoicePreview` (Financials-gated server-side — a
 *  reader without the grant never receives this at all, they get a 403). `basis` is the server's
 *  redaction-safe public snapshot (ids + quantities, no rates). */
export interface InvoiceDraft {
  lines: DraftLine[];
  subtotal: number;
  total: number;
  currency: string;
  basis: unknown;
}
